import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  deleteFarmViaDigisakaApi,
  displayBlockContextLabel,
  displayBlockLabel,
  displayFarmName,
  farmPublicId,
  farmRowsToBlockFeatureCollection,
  fetchExplorerAvailProvincesViaApi,
  fetchMonitoringFarmersListForUser,
  fetchPhilippinesCitiesViaApi,
  fetchSavedFieldsListForUser,
  fetchSavedFieldsPageForUser,
  formatFarmerFieldsLabel,
  formatFarmerMobileLabel,
  type ExplorerAvailProvince,
  type ExplorerFarmerOption,
  type PhilippinesCityRow,
} from '../../../config/digisakaApi';
import { formatMonitoringAreaLabel, withEnrichedMonitoringArea } from '../../../services/lguMonitoringArea';
import { digisakaAuthRequestInit, gptInferenceMultipartAuthHeaders } from '../../../config/digisakaGptInference';
import logo from "../../../assets/digisaka-official-logo.png";
import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';
import { downloadFarmBriefPdf, type FarmBriefLang } from '../../shared/utils/downloadFarmBrief';
import FarmReportEmailSchedule from '../../shared/components/FarmReportEmailSchedule';
import { useTheme } from '../../shared/theme/ThemeContext';
import { useAuth } from '../../user-dashboard/auth/AuthContext';
import { getSidebarTheme, getConfirmDialogTheme } from '../../shared/theme/themeStyles';
import WeatherAtmosphere from '../../shared/components/WeatherAtmosphere';
import WeatherMoodStrip from '../../shared/components/WeatherMoodStrip';
import ApiListSkeleton from '../../shared/components/ApiListSkeleton';
import {
  fetchFarmCurrentWeatherMap,
  type FarmCurrentWeather,
} from '../../shared/utils/farmCurrentWeather';
import {
  RISK_FILTER_OPTIONS,
  fetchFarmRiskStatuses,
  matchesRiskFilter,
  riskBadgeMeta,
  riskLevelOf,
  riskMarkerChipClass,
  riskSummaryText,
  type FarmRiskFilter,
  type FarmRiskStatus,
} from '../../shared/utils/farmRiskStatus';
import {
  FARM_LEVEL_MIN_ZOOM,
  farmLevelZoomTooLowMessage,
  isMapZoomedForFarmLevel,
} from '../../shared/utils/farmLevelMapZoom';
import {
  MAX_FARM_GROUP_MEMBERS,
  createFarmAnalysisGroup,
  deleteFarmAnalysisGroup,
  getFarmAnalysisGroup,
  listFarmAnalysisGroups,
  type FarmGroupSummary,
} from '../../../services/farmAnalysisGroups';

type FieldUploadFormatId = 'geojson' | 'shapefile' | 'kml' | 'kmz' | 'geopackage';

const FIELD_UPLOAD_FORMATS: Array<{
  id: FieldUploadFormatId;
  label: string;
  icon: string;
  accent: 'brand' | 'red' | 'purple';
  accept: string;
  multiple?: boolean;
  help: string;
}> = [
  {
    id: 'geojson',
    label: 'GeoJSON',
    icon: 'ri-file-code-line',
    accent: 'brand',
    accept: '.geojson,.json',
    help: 'Web map polygons (.geojson or .json). We convert to WGS84 if the file uses another CRS.',
  },
  {
    id: 'shapefile',
    label: 'Shapefile',
    icon: 'ri-file-zip-line',
    accent: 'brand',
    accept: '.shp,.dbf,.shx,.prj,.cpg,.zip',
    multiple: true,
    help: 'GIS shapefile — upload .shp with .dbf, .shx, and .prj, or a .zip of those files. The .prj is needed to convert CRS.',
  },
  {
    id: 'kml',
    label: 'KML',
    icon: 'ri-map-pin-line',
    accent: 'red',
    accept: '.kml',
    help: 'Google Earth or Google Maps export (.kml). Each polygon can be used as a farm boundary.',
  },
  {
    id: 'kmz',
    label: 'KMZ',
    icon: 'ri-file-zip-line',
    accent: 'red',
    accept: '.kmz',
    help: 'Zipped KML from Google Earth (.kmz). We unpack it, then treat it the same as KML.',
  },
  {
    id: 'geopackage',
    label: 'GeoPackage',
    icon: 'ri-database-2-line',
    accent: 'purple',
    accept: '.gpkg',
    help: 'QGIS or ArcGIS database file (.gpkg). All polygons are loaded so you can pick one.',
  },
];

/** Exterior rings for each block on a farm (webgis polygons / geometry). */
function fieldBlockRings(field: {
  polygons?: unknown;
  geometry?: unknown;
  _geometry?: unknown;
}): number[][][] {
  if (Array.isArray(field.polygons) && field.polygons.length > 0) {
    return (field.polygons as unknown[]).filter(
      (ring): ring is number[][] =>
        Array.isArray(ring) &&
        ring.length >= 3 &&
        Array.isArray(ring[0]) &&
        typeof (ring[0] as unknown[])[0] === 'number'
    );
  }
  const raw = (field.geometry ?? field._geometry) as
    | { type?: string; coordinates?: unknown; geometry?: { type?: string; coordinates?: unknown } }
    | null
    | undefined;
  const g = raw?.geometry ?? raw;
  if (!g || typeof g !== 'object') return [];
  if (g.type === 'Polygon' && Array.isArray(g.coordinates) && Array.isArray(g.coordinates[0])) {
    return [g.coordinates[0] as number[][]];
  }
  if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
    const rings: number[][][] = [];
    for (const poly of g.coordinates as unknown[]) {
      if (!Array.isArray(poly) || !poly[0]) continue;
      rings.push(poly[0] as number[][]);
    }
    return rings;
  }
  return [];
}

/** Admin/guest outbound catalog — farms loaded per scroll chunk (FastAPI page size). */
const OUTBOUND_SCROLL_PAGE_SIZE = 50;

/** Blocks/plots on a farm from webgis ``plot_count`` / ``polygons`` / geometry. */
function fieldBlockCount(field: {
  plot_count?: number | string | null;
  polygons?: unknown;
  geometry?: unknown;
  _geometry?: unknown;
}): number | null {
  const rings = fieldBlockRings(field);
  if (rings.length > 0) return rings.length;
  const fromPlot = Number(field.plot_count);
  if (Number.isFinite(fromPlot) && fromPlot > 0) return Math.floor(fromPlot);
  return null;
}

type FarmPlaceOption = { id: string; label: string };

function farmCityId(field: any): string {
  const id = field?.city_id ?? field?.city?.city_code ?? field?.city?.id;
  return id != null && String(id).trim() !== '' ? String(id) : '';
}

function farmCityLabel(field: any): string {
  return String(
    field?.city_name ||
      field?.municipality ||
      field?.city?.city ||
      field?.city?.name ||
      ''
  ).trim();
}

function farmProvinceLabel(field: any): string {
  return String(
    field?.province_name || field?.province?.province || field?.province?.name || ''
  ).trim();
}

/** Unique municipalities present on the user's farm list (for BLO sidebar filter). */
function municipalityOptionsFromFarms(rows: any[]): FarmPlaceOption[] {
  const byId = new Map<string, { label: string; province: string }>();
  for (const field of rows) {
    const id = farmCityId(field);
    const name = farmCityLabel(field);
    if (!id || !name) continue;
    if (!byId.has(id)) {
      byId.set(id, { label: name, province: farmProvinceLabel(field) });
    }
  }
  const nameCounts = new Map<string, number>();
  for (const { label } of byId.values()) {
    nameCounts.set(label, (nameCounts.get(label) || 0) + 1);
  }
  return Array.from(byId.entries())
    .map(([id, { label, province }]) => ({
      id,
      label:
        (nameCounts.get(label) || 0) > 1 && province ? `${label} (${province})` : label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onFieldSelect?: (field: any) => void;
  /** Focus a farm (zoom + Update field) without opening the analysis panel. */
  onFarmFocus?: (field: any) => void;
  onDeleteField?: (fieldId: any) => void;
  onAnalysisPanelToggle?: () => void;
  selectedField?: any;
  selectedFieldId?: any;

  // Drawing mode props
  isDrawingMode?: boolean;
  manualDrawEditing?: boolean;
  manualDrawRecoverable?: boolean;
  onDrawingModeToggle?: (isDrawing: boolean) => void;
  onConfirmManualDraw?: () => void;
  onCancelManualDrawEdit?: () => void;
  onResumeManualDrawEdit?: () => void;
  onDiscardManualDraw?: () => void;
  onRedrawManualDraw?: () => void;
  onDeleteManualDrawBlock?: () => void;
  drawnField?: any;
  /** Soft advisory when a drawn field looks outside GEE agricultural land. */
  agLandWarning?: string | null;
  onSaveDemoField?: () => void;
  onDeleteDemoField?: () => void;

  // File upload prop
  onFileUpload?: (geoJsonFeature: any) => void;

  // Field reload prop
  reloadFields?: boolean;
  onFieldsReloaded?: () => void;
  /** Fired after the saved-fields list is fetched (for map overview of all blocks). */
  onFieldsLoaded?: (
    fields: any[],
    options?: { fit?: boolean; groupFocus?: boolean; omitBoundaries?: boolean }
  ) => void;
  /** Risk levels for map / circular marker symbology (keyed by farm_id). */
  onFarmRiskStatuses?: (byFarmId: Record<string, FarmRiskStatus>) => void;
  /** Start the create-farm spotlight tour (Draw / AI / Upload). */
  onStartCreateFarmTour?: () => void;
  /** True when non-LGU saved-farm catalog is empty (after load). */
  onBloFarmCatalogEmptyChange?: (empty: boolean) => void;

  // Optional props for field interactions
  showData?: boolean;
  monitoringFieldId?: any;
  indexData?: any;
  vhiData?: any;
  processingFieldId?: any;
  processingType?: string;
  indexHeatMapData?: any;
  handleZoomToArea?: (fieldId: any) => void;
  handleMonitorSavedField?: (fieldId: any) => void;
  handleCloseAnalysis?: (fieldId: any) => void;
  handleGetIndexMapForDrawedField?: (fieldId: any) => void;
  setProcessingFieldId?: (fieldId: any) => void;
  setProcessingType?: (type: string) => void;

  // Multi-field analysis
  onStartMultiFieldAnalysis?: (fields: any[]) => void;
  /** When a saved group is opened — parent loads geometries, zooms to extent, labels farms. */
  onOpenFarmGroup?: (members: Array<{ farm_id: any; farm_name?: string | null }>) => void;
  /** Clear group polygons/labels from the map (exit group mode / back to My groups). */
  onClearFarmGroupMap?: () => void;
  /** When true, multi-field selection in the sidebar is temporarily locked (e.g. API in flight). */
  multiFieldSelectionLocked?: boolean;

  // Edit boundary mode props
  onEditBoundary?: (field: any) => void;
  /** Split farm via AI field delineation on the farm extent; user confirms Save. */
  onSplitFarm?: (field: any) => void;
  /** True while split-farm AI delineation is running. */
  isSplittingFarm?: boolean;
  /** Pending split review after AI detection (Save / Discard). */
  farmSplitReview?: {
    baseName: string;
    featureCount: number;
    step?: 'picking' | 'editing';
    selectedIndex?: number | null;
  } | null;
  isSavingFarmSplit?: boolean;
  onSaveFarmSplit?: () => void;
  onCancelFarmSplit?: () => void;
  onSelectSplitSubplot?: (index: number) => void;
  onStartAdjustSplitVertices?: () => void;
  onCancelSplitVertexEdit?: () => void;
  onConfirmSplitVertexEdit?: () => void;
  onSaveSelectedSplitSubplot?: () => void;
  onRemoveSplitSubplot?: () => void;
  /** Open large-scale Drone Data for this farm (reuses the same analysis card as the rail tool). */
  onOpenDroneData?: (field: any) => void;
  /** True when user is in "Adjust Farm Boundary" mode (shows different confirm button text) */
  isUpdatingMode?: boolean;

  /** AI field delineation (same API as large-scale field boundary delineation) */
  aiFieldDelineationStep?: 'idle' | 'instruction' | 'picking' | 'editing';
  aiFieldDelineationLoading?: boolean;
  aiFieldDelineationError?: string;
  aiFieldDelineationFieldCount?: number;
  aiDelineationSelectedIndex?: number | null;
  onAiFieldDelineationOpen?: () => void;
  onAiFieldDelineationClose?: () => void;
  onAiFieldDelineationProcess?: () => void;
  aiFieldDelineationInputMode?: 'mapView' | 'file';
  aiFieldDelineationModel?: 'yolo' | 'dino';
  onAiFieldDelineationModelChange?: (model: 'yolo' | 'dino') => void;
  onAiFieldDelineationInputModeChange?: (mode: 'mapView' | 'file') => void;
  aiFieldDelineationGeoTiffName?: string | null;
  onAiFieldDelineationGeoTiffChange?: (file: File | null) => void;
  onSelectDelineationField?: (index: number) => void;
  onStartAdjustDelineationVertices?: () => void;
  onCancelDelineationVertexEdit?: () => void;
  onConfirmDelineationAsField?: () => void;
  onUndoVertexEdit?: () => void;
  onRedoVertexEdit?: () => void;
  vertexEditCanUndo?: boolean;
  vertexEditCanRedo?: boolean;
  onUseSelectedDelineationField?: () => void;
  /** Multi-select mode for saving several detections as one farm (blocks). */
  aiDelineationMultiSelect?: boolean;
  aiDelineationCheckedIndexes?: number[];
  onToggleDelineationMultiSelect?: () => void;
  onSaveAllDelineationAsOneField?: () => void;
  onSaveCheckedDelineationAsOneField?: () => void;
  /** Remove one detected block from the map + list (by index, or selected if omitted). */
  onRemoveDelineationBlock?: (index?: number) => void;
  onRemoveCheckedDelineationBlocks?: () => void;
  onSelectAllDelineationBlocks?: () => void;
  onClearDelineationSelection?: () => void;
  onUseCheckedDelineationField?: () => void;
  showSavedFieldsDuringDelineation?: boolean;
  onShowSavedFieldsDuringDelineationChange?: (show: boolean) => void;
  onDismissOverlappingSavedDelineations?: () => void;
  /** `upload` = polygons from a field file; `ai` = detected boundaries. */
  aiFieldDelineationOrigin?: 'ai' | 'upload';
  aiFieldDelineationFieldLabels?: string[];

  /** Current map zoom — used to gate farm-level draw / map-view AI delineation */
  mapZoom?: number;

}

export default function Sidebar({
  isOpen,
  onToggle,
  onFieldSelect,
  onDeleteField,
  onAnalysisPanelToggle,
  selectedField,
  selectedFieldId,

  // Drawing mode props
  isDrawingMode = false,
  manualDrawEditing = false,
  manualDrawRecoverable = false,
  onDrawingModeToggle,
  onConfirmManualDraw,
  onCancelManualDrawEdit,
  onResumeManualDrawEdit,
  onDiscardManualDraw,
  onRedrawManualDraw,
  onDeleteManualDrawBlock,
  drawnField,
  agLandWarning = null,
  onSaveDemoField,
  onDeleteDemoField,

  // File upload prop
  onFileUpload,

  // Field reload props
  reloadFields = false,
  onFieldsReloaded,
  onFieldsLoaded,
  onFarmRiskStatuses,
  onStartCreateFarmTour,
  onBloFarmCatalogEmptyChange,

  // Optional props
  showData,
  monitoringFieldId,
  indexData,
  vhiData,
  processingFieldId,
  processingType,
  indexHeatMapData,
  handleZoomToArea = () => {},
  handleMonitorSavedField = () => {},
  handleCloseAnalysis = () => {},
  handleGetIndexMapForDrawedField = () => {},
  setProcessingFieldId = () => {},
  setProcessingType = () => {},
  onStartMultiFieldAnalysis,
  onOpenFarmGroup,
  onClearFarmGroupMap,
  multiFieldSelectionLocked = false,
  onEditBoundary,
  onSplitFarm,
  isSplittingFarm = false,
  farmSplitReview = null,
  isSavingFarmSplit = false,
  onSaveFarmSplit,
  onCancelFarmSplit,
  onSelectSplitSubplot,
  onStartAdjustSplitVertices,
  onCancelSplitVertexEdit,
  onConfirmSplitVertexEdit,
  onSaveSelectedSplitSubplot,
  onRemoveSplitSubplot,
  onOpenDroneData,
  isUpdatingMode = false,

  aiFieldDelineationStep = 'idle',
  aiFieldDelineationLoading = false,
  aiFieldDelineationError = '',
  aiFieldDelineationFieldCount = 0,
  aiDelineationSelectedIndex = null,
  onAiFieldDelineationOpen,
  onAiFieldDelineationClose,
  onAiFieldDelineationProcess,
  aiFieldDelineationInputMode = 'mapView',
  onAiFieldDelineationInputModeChange,
  aiFieldDelineationGeoTiffName = null,
  onAiFieldDelineationGeoTiffChange,
  onSelectDelineationField,
  onStartAdjustDelineationVertices,
  onCancelDelineationVertexEdit,
  onConfirmDelineationAsField,
  onUndoVertexEdit,
  onRedoVertexEdit,
  vertexEditCanUndo = false,
  vertexEditCanRedo = false,
  onUseSelectedDelineationField,
  aiDelineationMultiSelect = false,
  aiDelineationCheckedIndexes = [],
  onToggleDelineationMultiSelect,
  onSaveAllDelineationAsOneField,
  onSaveCheckedDelineationAsOneField,
  onRemoveDelineationBlock,
  onRemoveCheckedDelineationBlocks,
  onSelectAllDelineationBlocks,
  onClearDelineationSelection,
  onUseCheckedDelineationField,
  showSavedFieldsDuringDelineation = false,
  onShowSavedFieldsDuringDelineationChange,
  onDismissOverlappingSavedDelineations,
  aiFieldDelineationOrigin = 'ai',
  aiFieldDelineationFieldLabels = [],
  mapZoom,
}: SidebarProps) {
  const { isDark } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const modHint =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)
      ? '⌘'
      : 'Ctrl';
  const checkedCount = aiDelineationCheckedIndexes.length;
  const isLguUser = String(user?.role || '').toLowerCase() === 'lgu';
  const isAdminUser =
    user?.is_superuser === true || String(user?.role || '').toLowerCase() === 'admin';
  /** Admin/guest use outbound summary list by default; optional full geometry for map. */
  const usesOutboundFarmCatalog =
    isAdminUser || user?.is_system_guest === true;
  const [lguMonitoringLabel, setLguMonitoringLabel] = useState<string | null>(() =>
    formatMonitoringAreaLabel(user?.monitoring_area)
  );

  useEffect(() => {
    if (!isLguUser) {
      setLguMonitoringLabel(null);
      return;
    }
    let cancelled = false;
    const existing = formatMonitoringAreaLabel(user?.monitoring_area);
    if (existing) {
      setLguMonitoringLabel(existing);
      return;
    }
    void withEnrichedMonitoringArea({
      role: user?.role,
      monitoring_area: user?.monitoring_area ?? null,
    }).then((enriched) => {
      if (cancelled) return;
      setLguMonitoringLabel(formatMonitoringAreaLabel(enriched.monitoring_area));
    });
    return () => {
      cancelled = true;
    };
  }, [isLguUser, user?.role, user?.monitoring_area]);
  const sb = getSidebarTheme(isDark);
  const cd = getConfirmDialogTheme(isDark);
  const isFarmLevelMapZoom = isMapZoomedForFarmLevel(mapZoom);
  const farmLevelZoomHint = farmLevelZoomTooLowMessage(mapZoom);
  const [showFileUploadMenu, setShowFileUploadMenu] = useState(false);
  const [uploadFormatHelpId, setUploadFormatHelpId] = useState<FieldUploadFormatId | null>(null);
  const [showFarmAnalysisTooltip, setShowFarmAnalysisTooltip] = useState(true);
  /** Group farms for bulk analysis — replaces auto-opening Multi Field Analysis. */
  const [isFarmGroupMode, setIsFarmGroupMode] = useState(false);
  /** 'groups' = only saved groups; 'pick' = select farms (max 20). */
  const [farmGroupView, setFarmGroupView] = useState<'groups' | 'pick'>('groups');
  const [selectedMultiFieldIds, setSelectedMultiFieldIds] = useState<any[]>([]);
  const [selectedMultiFields, setSelectedMultiFields] = useState<any[]>([]);
  const [farmGroups, setFarmGroups] = useState<FarmGroupSummary[]>([]);
  const [farmGroupsLoading, setFarmGroupsLoading] = useState(false);
  const [farmGroupsError, setFarmGroupsError] = useState<string | null>(null);
  const [showSaveGroupModal, setShowSaveGroupModal] = useState(false);
  const [saveGroupName, setSaveGroupName] = useState('');
  const [saveGroupDescription, setSaveGroupDescription] = useState('');
  const [saveGroupBusy, setSaveGroupBusy] = useState(false);
  const [saveGroupError, setSaveGroupError] = useState<string | null>(null);
  const [groupActionBusyId, setGroupActionBusyId] = useState<string | null>(null);
  /** When set, sidebar lists only this group's farms (all other farms hidden). */
  const [openedFarmGroup, setOpenedFarmGroup] = useState<{ id: string; name: string } | null>(null);
  const [openedGroupMembers, setOpenedGroupMembers] = useState<any[]>([]);

  const isOpenedGroupFocus = Boolean(openedFarmGroup && farmGroupView === 'pick');
  /** Checkbox multi-select only while building a new group — opened groups use the normal field list. */
  const isMultiFieldMode = isFarmGroupMode && farmGroupView === 'pick' && !isOpenedGroupFocus;
  /**
   * Always use LGU-style layout: field tools / filters stay put, farm list scrolls
   * in the remaining height (no Show All / Show Less).
   */
  const isFarmListFocusMode = true;

  const clearOpenedFarmGroup = () => {
    setOpenedFarmGroup(null);
    setOpenedGroupMembers([]);
  };

  const refreshFarmGroups = async () => {
    setFarmGroupsLoading(true);
    setFarmGroupsError(null);
    try {
      const items = await listFarmAnalysisGroups();
      setFarmGroups(items);
    } catch (err: any) {
      setFarmGroupsError(err?.message || 'Failed to load farm groups');
    } finally {
      setFarmGroupsLoading(false);
    }
  };

  useEffect(() => {
    if (!isFarmGroupMode || farmGroupView !== 'groups') return;
    void refreshFarmGroups();
  }, [isFarmGroupMode, farmGroupView]);

  const exitFarmGroupMode = () => {
    setIsFarmGroupMode(false);
    setFarmGroupView('groups');
    setSelectedMultiFieldIds([]);
    setSelectedMultiFields([]);
    clearOpenedFarmGroup();
    setShowSaveGroupModal(false);
    setSaveGroupName('');
    setSaveGroupDescription('');
    setSaveGroupError(null);
    onStartMultiFieldAnalysis?.([]);
    onClearFarmGroupMap?.();
  };

  const enterFarmGroupMode = () => {
    setIsFarmGroupMode(true);
    setFarmGroupView('groups');
    setSelectedMultiFieldIds([]);
    setSelectedMultiFields([]);
    clearOpenedFarmGroup();
    setSaveGroupError(null);
  };

  const openSaveGroupModal = () => {
    if (selectedMultiFields.length < 1) return;
    setSaveGroupName('');
    setSaveGroupDescription('');
    setSaveGroupError(null);
    setShowSaveGroupModal(true);
  };

  const handleSaveFarmGroup = async () => {
    const name = saveGroupName.trim();
    if (!name) {
      setSaveGroupError('Group name is required');
      return;
    }
    if (selectedMultiFields.length < 1) {
      setSaveGroupError('Select at least one farm');
      return;
    }
    if (selectedMultiFields.length > MAX_FARM_GROUP_MEMBERS) {
      setSaveGroupError(`A group can have at most ${MAX_FARM_GROUP_MEMBERS} farms`);
      return;
    }
    setSaveGroupBusy(true);
    setSaveGroupError(null);
    try {
      await createFarmAnalysisGroup({
        name,
        description: saveGroupDescription.trim() || undefined,
        members: selectedMultiFields.map((f) => ({
          farm_id: String(f.farm_id),
          farm_name: f.farm_name || f.name || null,
          province: f.province_name || f.province || null,
          municipality: f.city_name || f.municipality || null,
        })),
      });
      setShowSaveGroupModal(false);
      setSaveGroupName('');
      setSaveGroupDescription('');
      setSelectedMultiFieldIds([]);
      setSelectedMultiFields([]);
      clearOpenedFarmGroup();
      setFarmGroupView('groups');
      await refreshFarmGroups();
    } catch (err: any) {
      setSaveGroupError(err?.message || 'Failed to save group');
    } finally {
      setSaveGroupBusy(false);
    }
  };

  const handleOpenGroup = async (groupId: string) => {
    setGroupActionBusyId(groupId);
    setFarmGroupsError(null);
    try {
      const detail = await getFarmAnalysisGroup(groupId);
      const members = (detail.members || []).map((m) => {
        const fromList = fields.find((f) => String(f.farm_id) === String(m.farm_id));
        if (fromList) return fromList;
        return {
          farm_id: m.farm_id,
          farm_name: m.farm_name || displayFarmName(m as Record<string, unknown>),
          province_name: m.province || undefined,
          city_name: m.municipality || undefined,
        };
      });
      setOpenedFarmGroup({ id: detail.id, name: detail.name });
      setOpenedGroupMembers(members);
      setSelectedMultiFieldIds(members.map((m) => m.farm_id));
      setSelectedMultiFields(members);
      setSearchQuery('');
      setFarmGroupView('pick');
      onOpenFarmGroup?.(
        members.map((m) => ({
          farm_id: m.farm_id,
          farm_name: m.farm_name || null,
        }))
      );
    } catch (err: any) {
      setFarmGroupsError(err?.message || 'Failed to open group');
    } finally {
      setGroupActionBusyId(null);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm('Delete this farm group? This cannot be undone.')) return;
    setGroupActionBusyId(groupId);
    setFarmGroupsError(null);
    try {
      await deleteFarmAnalysisGroup(groupId);
      await refreshFarmGroups();
    } catch (err: any) {
      setFarmGroupsError(err?.message || 'Failed to delete group');
    } finally {
      setGroupActionBusyId(null);
    }
  };

  const handleStartMultiFieldFromSelection = () => {
    const fieldsForAnalysis = isOpenedGroupFocus ? openedGroupMembers : selectedMultiFields;
    if (fieldsForAnalysis.length < 2 || !onStartMultiFieldAnalysis) return;
    onStartMultiFieldAnalysis(fieldsForAnalysis);
  };

  // BLO: municipality filter is client-side from the loaded catalog.
  // Admin: province + municipality refetch digisaka webgis-farms (server-side filters).
  const [selectedProvinceId, setSelectedProvinceId] = useState('all');
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState('all');
  const [adminProvinceOptions, setAdminProvinceOptions] = useState<ExplorerAvailProvince[]>([]);
  const [adminCityOptions, setAdminCityOptions] = useState<PhilippinesCityRow[]>([]);
  const [adminLocationLoading, setAdminLocationLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  /** Set when the farms API fails — distinct from an legitimately empty catalog. */
  const [fieldsLoadError, setFieldsLoadError] = useState<string | null>(null);
  /**
   * Farm outlines on the map.
   * null = role default: on for LGU (few farms), off for admin/guest (full catalog lags).
   * Admin/guest ``true`` fetches ``fields=full``.
   */
  const [loadFullFarmGeometry, setLoadFullFarmGeometry] = useState<boolean | null>(null);
  const showFarmBoundaries = loadFullFarmGeometry ?? isLguUser;
  /** Outbound catalog total from FastAPI pagination (correct count without loading every row). */
  const [farmCatalogTotal, setFarmCatalogTotal] = useState<number | null>(null);
  const [farmCatalogHasMore, setFarmCatalogHasMore] = useState(false);
  const [isLoadingMoreFields, setIsLoadingMoreFields] = useState(false);
  const farmCatalogPageRef = useRef(1);
  const loadingMoreFieldsRef = useRef(false);

  // Risk level per farm, rolled up from every active alert by `/api/farm-risk-status/`.
  const [riskFilter, setRiskFilter] = useState<FarmRiskFilter>('all');
  const [riskStatusById, setRiskStatusById] = useState<Record<string, FarmRiskStatus>>({});

  // Field-name / field-ID search. Scoped to this sidebar only; the top-right header search
  // is a separate Nominatim place/coordinate search (see `SearchBar` in `HomePage`).
  const [searchQuery, setSearchQuery] = useState('');
  /** Debounced value forwarded to FastAPI for admin/guest server-side search. */
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 400);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<any>(null);
  const [fields, setFields] = useState<any[]>([]);
  /** Map pin numbers (1-based) matching overview markers — keyed by farm_id. */
  const [farmMarkerById, setFarmMarkerById] = useState<Record<string, number>>({});
  /** Farm card expanded to list block1 / block2 / … */
  const [expandedFarmId, setExpandedFarmId] = useState<string | null>(null);
  const [briefDownloadLang, setBriefDownloadLang] = useState<FarmBriefLang | null>(null);
  const [briefDialog, setBriefDialog] = useState<{ farm_id: string; farm_name?: string } | null>(null);
  const [briefDialogError, setBriefDialogError] = useState<string | null>(null);

  const openBriefDialog = (field: { farm_id?: string | number; farm_name?: string }) => {
    const farmId = String(field.farm_id ?? '');
    if (!/^\d+$/.test(farmId) || briefDownloadLang) return;
    setBriefDialogError(null);
    setBriefDialog({ farm_id: farmId, farm_name: field.farm_name });
  };

  const downloadFieldBrief = async (lang: FarmBriefLang) => {
    if (!briefDialog || briefDownloadLang) return;
    setBriefDownloadLang(lang);
    setBriefDialogError(null);
    try {
      await downloadFarmBriefPdf(briefDialog.farm_id, lang, briefDialog.farm_name);
      setBriefDialog(null);
    } catch (error) {
      console.error('Farm brief download failed:', error);
      setBriefDialogError('Could not download the report. Try again.');
    } finally {
      setBriefDownloadLang(null);
    }
  };
  /** Live OpenWeather for Saved Fields cards currently in the sidebar scroll viewport. */
  const [fieldWeatherById, setFieldWeatherById] = useState<Record<string, FarmCurrentWeather>>({});
  /** Farm cards intersecting the list scroll area (Y-axis) — weather is fetched only for these. */
  const [viewportWeatherFarmIds, setViewportWeatherFarmIds] = useState<string[]>([]);
  const fieldsScrollRef = useRef<HTMLDivElement | null>(null);
  const visibleWeatherIdsRef = useRef<Set<string>>(new Set());
  const weatherObserverRef = useRef<IntersectionObserver | null>(null);
  /** When true, next filtered→map sync should fit bounds (API refetch / location filters). */
  const fitMapOnNextFilterSyncRef = useRef(false);

  /** LGU monitoring catalog: fields vs farmers. */
  const [lguCatalogTab, setLguCatalogTab] = useState<'fields' | 'farmers'>('fields');
  const [farmers, setFarmers] = useState<ExplorerFarmerOption[]>([]);
  const [isLoadingFarmers, setIsLoadingFarmers] = useState(false);
  const [farmersError, setFarmersError] = useState<string | null>(null);
  /** When set, Fields tab shows only this farmer's farms. */
  const [farmerFilter, setFarmerFilter] = useState<{
    farmer_id: string | number;
    name: string;
  } | null>(null);

  // Date filter options
  const dateFilterOptions = [
    { label: "All Fields", value: null },
    { label: "Today's Fields", value: "today" },
    { label: "Last 2 Days", value: "2days" },
    { label: "Last 7 Days", value: "7days" },
    { label: "Last 30 Days", value: "30days" },
  ];

  const [isUploadingFieldFile, setIsUploadingFieldFile] = useState(false);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileType: 'geojson' | 'shapefile' | 'kml' | 'kmz' | 'geopackage' | 'gpkg'
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setShowFileUploadMenu(false);
    setUploadFormatHelpId(null);
    setIsUploadingFieldFile(true);

    const formData = new FormData();
    const apiFileType = fileType === 'gpkg' ? 'geopackage' : fileType;
    formData.append('fileType', apiFileType);
    for (let i = 0; i < files.length; i++) {
      formData.append('shapefile', files[i]);
    }

    try {
      const response = await fetch(
        DIGISAKA_API_ORIGIN + '/api/shapefile-kml-to-geojson/',
        digisakaAuthRequestInit({
          method: 'POST',
          headers: gptInferenceMultipartAuthHeaders(),
          body: formData,
        })
      );

      if (!response.ok) {
        let message = 'Error uploading file';
        try {
          const errorData = await response.json();
          const detail = errorData.detail ?? errorData.error;
          if (typeof detail === 'string' && detail.trim()) {
            message = detail;
          } else if (Array.isArray(detail)) {
            message = detail
              .map((item: { msg?: string }) => item?.msg || JSON.stringify(item))
              .join('; ');
          }
        } catch {
          /* use fallback */
        }
        throw new Error(message);
      }

      notifyInferenceComputeUsage(response);

      const result = await response.json();
      const geoJsonObject =
        typeof result.geojson === 'string' ? JSON.parse(result.geojson) : result.geojson;

      if (
        !geoJsonObject ||
        geoJsonObject.type !== 'FeatureCollection' ||
        !Array.isArray(geoJsonObject.features) ||
        geoJsonObject.features.length === 0
      ) {
        throw new Error('No polygon features found in the uploaded file.');
      }

      onFileUpload?.({
        type: 'FeatureCollection',
        features: geoJsonObject.features,
        reprojected: Boolean(result.reprojected),
        source_crs: result.source_crs ?? null,
        feature_count: result.feature_count ?? geoJsonObject.features.length,
      });
    } catch (error: unknown) {
      console.error('Upload error:', error);
      onFileUpload?.({
        error: error instanceof Error ? error.message : 'Unknown error',
        type: fileType,
      });
    } finally {
      setIsUploadingFieldFile(false);
      event.target.value = '';
    }
  };

  const formatFieldsLoadError = (error: unknown): string => {
    if (error && typeof error === 'object') {
      const ax = error as {
        response?: { status?: number; data?: { detail?: string; message?: string } };
        message?: string;
        code?: string;
      };
      if (!ax.response) {
        return 'Could not reach the server. Check your internet connection and try again.';
      }
      const detail = ax.response.data?.detail || ax.response.data?.message;
      if (detail && String(detail).trim()) return String(detail).trim();
      if (ax.response.status === 401) {
        return 'Your session expired. Please sign in again.';
      }
      if (ax.response.status && ax.response.status >= 500) {
        return 'The farm service is temporarily unavailable. Please try again.';
      }
    }
    if (error instanceof Error && error.message.trim()) {
      return error.message.trim();
    }
    return 'Could not load farms. Check your connection and try again.';
  };

  const buildFieldsFetchOptions = useCallback((): Parameters<
    typeof fetchSavedFieldsListForUser
  >[1] => {
    let startdate: string | undefined;
    let enddate: string | undefined;
    if (dateFilter) {
      const end = new Date();
      const start = new Date(end);
      switch (dateFilter) {
        case 'today':
          break;
        case '2days':
          start.setDate(end.getDate() - 1);
          break;
        case '7days':
          start.setDate(end.getDate() - 7);
          break;
        case '30days':
          start.setDate(end.getDate() - 29);
          break;
        default:
          break;
      }
      const formatDate = (d: Date) => d.toISOString().split('T')[0];
      startdate = formatDate(start);
      enddate = formatDate(end);
    }

    const fetchOptions: Parameters<typeof fetchSavedFieldsListForUser>[1] = {
      startdate,
      enddate,
    };
    if (isAdminUser) {
      if (selectedProvinceId !== 'all') {
        fetchOptions.province_id = selectedProvinceId;
      }
      if (selectedMunicipalityId !== 'all') {
        fetchOptions.city_id = selectedMunicipalityId;
      }
    }
    if (usesOutboundFarmCatalog && loadFullFarmGeometry) {
      fetchOptions.fields = 'full';
    }
    if (usesOutboundFarmCatalog) {
      if (debouncedSearchQuery) {
        fetchOptions.search = debouncedSearchQuery;
      }
      if (riskFilter !== 'all') {
        fetchOptions.risk_level = riskFilter;
      }
    }
    return fetchOptions;
  }, [
    dateFilter,
    isAdminUser,
    selectedProvinceId,
    selectedMunicipalityId,
    usesOutboundFarmCatalog,
    loadFullFarmGeometry,
    debouncedSearchQuery,
    riskFilter,
  ]);

  const mergeUniqueFarmRows = useCallback((prev: any[], rows: any[]) => {
    const seen = new Set(prev.map((f) => String(f.farm_id)));
    const next = [...prev];
    for (const row of rows) {
      if (row?.farm_id == null) continue;
      const key = String(row.farm_id);
      if (seen.has(key)) continue;
      seen.add(key);
      next.push(row);
    }
    return next;
  }, []);

  // Fetch fields via Digisaka FastAPI GET /api/webgis/farms (server routes admin vs customer).
  // Admin/guest summary: first page only; more load on scroll. Map-all-boundaries still uses fetch_all.
  const fetchFields = async () => {
    setIsLoading(true);
    setFieldsLoadError(null);
    setFarmCatalogHasMore(false);
    farmCatalogPageRef.current = 1;
    try {
      const fetchOptions = buildFieldsFetchOptions();
      const useScrollCatalog =
        usesOutboundFarmCatalog && !loadFullFarmGeometry;

      if (useScrollCatalog) {
        const result = await fetchSavedFieldsPageForUser(user, {
          ...fetchOptions,
          fetch_all_pages: false,
          page: 1,
          per_page: OUTBOUND_SCROLL_PAGE_SIZE,
        });
        setFields(result.rows);
        const total = result.pagination?.total ?? result.rows.length;
        const currentPage = result.pagination?.current_page ?? 1;
        const lastPage = result.pagination?.last_page ?? 1;
        setFarmCatalogTotal(total);
        farmCatalogPageRef.current = currentPage;
        setFarmCatalogHasMore(currentPage < lastPage);
      } else {
        setFarmCatalogTotal(null);
        const rows = await fetchSavedFieldsListForUser(user, fetchOptions);
        setFields(rows);
      }

      setFieldsLoadError(null);
      fitMapOnNextFilterSyncRef.current = true;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401) {
        setFieldsLoadError(formatFieldsLoadError(error));
      } else {
        setFields([]);
        setFieldsLoadError(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreFields = useCallback(async () => {
    if (!usesOutboundFarmCatalog || loadFullFarmGeometry) return;
    if (!farmCatalogHasMore || isLoading || isLoadingMoreFields) return;
    if (loadingMoreFieldsRef.current) return;

    loadingMoreFieldsRef.current = true;
    setIsLoadingMoreFields(true);
    try {
      const nextPage = farmCatalogPageRef.current + 1;
      const result = await fetchSavedFieldsPageForUser(user, {
        ...buildFieldsFetchOptions(),
        fetch_all_pages: false,
        page: nextPage,
        per_page: OUTBOUND_SCROLL_PAGE_SIZE,
      });
      setFields((prev) => mergeUniqueFarmRows(prev, result.rows));
      const currentPage = result.pagination?.current_page ?? nextPage;
      const lastPage = result.pagination?.last_page ?? currentPage;
      farmCatalogPageRef.current = currentPage;
      setFarmCatalogHasMore(currentPage < lastPage);
      if (result.pagination?.total != null) {
        setFarmCatalogTotal(result.pagination.total);
      }
    } catch (error) {
      console.warn('Load more farms failed:', error);
    } finally {
      loadingMoreFieldsRef.current = false;
      setIsLoadingMoreFields(false);
    }
  }, [
    usesOutboundFarmCatalog,
    loadFullFarmGeometry,
    farmCatalogHasMore,
    isLoading,
    isLoadingMoreFields,
    user,
    buildFieldsFetchOptions,
    mergeUniqueFarmRows,
  ]);

  const handleFieldsListScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!usesOutboundFarmCatalog || loadFullFarmGeometry || !farmCatalogHasMore) return;
      const el = event.currentTarget;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 160) {
        void loadMoreFields();
      }
    },
    [usesOutboundFarmCatalog, loadFullFarmGeometry, farmCatalogHasMore, loadMoreFields]
  );

  // Load province list for admin location filters.
  useEffect(() => {
    if (!isAdminUser) {
      setAdminProvinceOptions([]);
      return;
    }
    let cancelled = false;
    void fetchExplorerAvailProvincesViaApi()
      .then((rows) => {
        if (!cancelled) setAdminProvinceOptions(rows);
      })
      .catch((err) => {
        console.warn('Failed to load admin province options:', err);
        if (!cancelled) setAdminProvinceOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, user?.id]);

  // Load municipalities when admin selects a province.
  useEffect(() => {
    if (!isAdminUser || selectedProvinceId === 'all') {
      setAdminCityOptions([]);
      return;
    }
    let cancelled = false;
    setAdminLocationLoading(true);
    void fetchPhilippinesCitiesViaApi(selectedProvinceId)
      .then((rows) => {
        if (!cancelled) setAdminCityOptions(rows);
      })
      .catch((err) => {
        console.warn('Failed to load admin city options:', err);
        if (!cancelled) setAdminCityOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAdminLocationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, selectedProvinceId]);

  // Fetch fields: admin refetches on province/municipality/date/search/risk; BLO/LGU on date only.
  useEffect(() => {
    if (authLoading || !user) {
      if (!authLoading && !user) {
        setFields([]);
        setFieldsLoadError(null);
        setIsLoading(false);
      }
      return;
    }
    fetchFields();
  }, [
    dateFilter,
    user?.id,
    user?.external_user_id,
    isLguUser,
    isAdminUser,
    isAdminUser ? selectedProvinceId : null,
    isAdminUser ? selectedMunicipalityId : null,
    usesOutboundFarmCatalog ? loadFullFarmGeometry : null,
    usesOutboundFarmCatalog ? debouncedSearchQuery : null,
    usesOutboundFarmCatalog ? riskFilter : null,
    authLoading,
  ]);

  // Fit map when BLO municipality filter changes (client-side, no refetch).
  useEffect(() => {
    if (isLguUser || isAdminUser) return;
    fitMapOnNextFilterSyncRef.current = true;
  }, [selectedMunicipalityId, isLguUser, isAdminUser]);

  const fetchFarmers = async () => {
    if (!isLguUser) {
      setFarmers([]);
      setFarmersError(null);
      return;
    }
    setIsLoadingFarmers(true);
    setFarmersError(null);
    try {
      const rows = await fetchMonitoringFarmersListForUser();
      setFarmers(rows);
    } catch (error: unknown) {
      console.error('Error fetching farmers:', error);
      setFarmers([]);
      const ax = error as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = ax.response?.data?.detail;
      setFarmersError(
        typeof detail === 'string'
          ? detail
          : ax.message || 'Failed to load farmers for this monitoring area.'
      );
    } finally {
      setIsLoadingFarmers(false);
    }
  };

  // Fetch LGU farmers with fields list
  useEffect(() => {
    if (!isLguUser) return;
    void fetchFarmers();
  }, [isLguUser, user?.id, reloadFields]);

  // Handle field reload trigger
  useEffect(() => {
    if (reloadFields && user) {
      fetchFields();
      if (onFieldsReloaded) {
        onFieldsReloaded();
      }
    }
  }, [reloadFields]);

  // Create combined fields list with drawn field at top if it exists.
  // When a saved group is open, show only that group's farms (hide the rest).
  const allFields = useMemo(
    () =>
      isOpenedGroupFocus
        ? openedGroupMembers
        : drawnField
          ? [drawnField, ...fields]
          : fields,
    [isOpenedGroupFocus, openedGroupMembers, drawnField, fields]
  );

  // Optional LGU farmer filter on fields list
  const farmerScopedFields = useMemo(
    () =>
      isLguUser && farmerFilter
        ? allFields.filter(
            (field) => String(field.farmer_id ?? '') === String(farmerFilter.farmer_id)
          )
        : allFields,
    [isLguUser, farmerFilter, allFields]
  );

  // BLO: municipality options from loaded catalog; admin: from philippines-cities API.
  const municipalityOptions = useMemo(() => {
    if (isLguUser) return [];
    if (isAdminUser) {
      return adminCityOptions.map((city) => ({
        id: city.city_code,
        label: city.city_name,
      }));
    }
    return municipalityOptionsFromFarms(fields);
  }, [isLguUser, isAdminUser, adminCityOptions, fields]);

  // Drop stale municipality selection when the catalog no longer includes it
  useEffect(() => {
    if (isLguUser) return;
    if (
      selectedMunicipalityId !== 'all' &&
      !municipalityOptions.some((o) => o.id === selectedMunicipalityId)
    ) {
      setSelectedMunicipalityId('all');
    }
  }, [isLguUser, municipalityOptions, selectedMunicipalityId]);

  // BLO municipality filter (client-side). Admin uses server-side webgis-farms filters.
  const locationScopedFields = useMemo(() => {
    if (isLguUser || isAdminUser || selectedMunicipalityId === 'all') return farmerScopedFields;
    return farmerScopedFields.filter((field) => {
      if (field?.isDemo || field?.isUnsaved) return true;
      return farmCityId(field) === selectedMunicipalityId;
    });
  }, [isLguUser, isAdminUser, farmerScopedFields, selectedMunicipalityId]);

  /** Admin/guest search + risk are resolved on FastAPI / digisaka, not in the browser. */
  const usesServerFieldFilters = usesOutboundFarmCatalog;

  // Filter fields by search query (name or ID). Admin/guest use server-side search.
  const searchedFields = useMemo(() => {
    if (usesServerFieldFilters) return locationScopedFields;
    const q = searchQuery.trim();
    if (!q) return locationScopedFields;
    const searchLower = q.toLowerCase();
    return locationScopedFields.filter((field) => {
      const fieldName = displayFarmName(field as Record<string, unknown>).toLowerCase();
      const fieldId = field.farm_id?.toString().toLowerCase() || '';
      const publicId = farmPublicId(field).toLowerCase();
      const uniqueRaw = String(field.farm_unique_id ?? '').toLowerCase();
      const farmerName = (field.farmer_name || '').toLowerCase();
      return (
        fieldName.includes(searchLower) ||
        fieldId.includes(searchLower) ||
        publicId.includes(searchLower) ||
        uniqueRaw.includes(searchLower) ||
        farmerName.includes(searchLower)
      );
    });
  }, [usesServerFieldFilters, locationScopedFields, searchQuery]);

  // Risk levels are only fetched for saved farms, so demo and just-drawn fields would
  // always read as "none" — keep them visible instead of hiding what the user just drew.
  const filteredFields = useMemo(
    () => {
      if (usesServerFieldFilters) return searchedFields;
      return riskFilter === 'all'
        ? searchedFields
        : searchedFields.filter((field) => {
            if (field?.isDemo || field?.isUnsaved) return true;
            const level = riskLevelOf(riskStatusById[String(field.farm_id)]);
            return matchesRiskFilter(level, riskFilter);
          });
    },
    [usesServerFieldFilters, searchedFields, riskFilter, riskStatusById]
  );

  // Keep map markers in sync with the sidebar-filtered farm list (search / risk / farmer / group).
  useEffect(() => {
    const mapRows = filteredFields.filter((f) => !f?.isDemo && !f?.isUnsaved);
    const omitMapBoundaries = isLguUser && !showFarmBoundaries && !isOpenedGroupFocus;
    const rowsForMarkers = omitMapBoundaries
      ? mapRows.map((row) =>
          row && typeof row === 'object'
            ? { ...row, polygons: undefined, geometry: undefined, geojson: undefined, _geometry: undefined }
            : row
        )
      : mapRows;
    setFarmMarkerById(farmRowsToBlockFeatureCollection(rowsForMarkers).markerByFarmId);
    const fit = fitMapOnNextFilterSyncRef.current;
    fitMapOnNextFilterSyncRef.current = false;
    onFieldsLoaded?.(mapRows, {
      fit,
      groupFocus: isOpenedGroupFocus,
      omitBoundaries: omitMapBoundaries,
    });
  }, [filteredFields, isOpenedGroupFocus, onFieldsLoaded, isLguUser, showFarmBoundaries]);

  // Tell parent when BLO catalog has zero saved farms (for create-farm tour).
  useEffect(() => {
    if (isLguUser || isLoading) return;
    const catalogEmpty =
      usesOutboundFarmCatalog && farmCatalogTotal != null
        ? farmCatalogTotal === 0
        : fields.length === 0;
    onBloFarmCatalogEmptyChange?.(catalogEmpty);
  }, [
    isLguUser,
    isLoading,
    fields.length,
    usesOutboundFarmCatalog,
    farmCatalogTotal,
    onBloFarmCatalogEmptyChange,
  ]);

  const searchedFarmers = searchQuery.trim()
    ? farmers.filter((farmer) => {
        const q = searchQuery.toLowerCase();
        const name = (farmer.name || farmer.label || '').toLowerCase();
        const id = String(farmer.farmer_id ?? '').toLowerCase();
        const mobile = String(farmer.mobile || '').toLowerCase();
        const email = String(farmer.email || '').toLowerCase();
        return (
          name.includes(q) || id.includes(q) || mobile.includes(q) || email.includes(q)
        );
      })
    : farmers;

  const showFarmersCatalog = isLguUser && lguCatalogTab === 'farmers';

  const fieldsCountLabel = useMemo(() => {
    if (showFarmersCatalog) return String(searchedFarmers.length);
    if (
      usesOutboundFarmCatalog &&
      !loadFullFarmGeometry &&
      farmCatalogTotal != null
    ) {
      return farmCatalogTotal.toLocaleString();
    }
    return filteredFields.length.toLocaleString();
  }, [
    showFarmersCatalog,
    searchedFarmers.length,
    usesOutboundFarmCatalog,
    loadFullFarmGeometry,
    farmCatalogTotal,
    filteredFields.length,
  ]);

  const currentFarmers = searchedFarmers;

  /**
   * OpenWeather only for farm cards currently visible in the sidebar scroll viewport.
   * Scrolling (Y) reveals more cards → those get fetched; off-screen cards are skipped.
   */
  const syncViewportWeatherIds = useCallback(() => {
    const next = Array.from(visibleWeatherIdsRef.current).sort();
    setViewportWeatherFarmIds((prev) => {
      if (prev.length === next.length && prev.every((id, i) => id === next[i])) {
        return prev;
      }
      return next;
    });
  }, []);

  const fieldsScrollCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (weatherObserverRef.current) {
        weatherObserverRef.current.disconnect();
        weatherObserverRef.current = null;
      }
      fieldsScrollRef.current = node;
      visibleWeatherIdsRef.current = new Set();
      if (!node || isFarmGroupMode) {
        setViewportWeatherFarmIds([]);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          let changed = false;
          for (const entry of entries) {
            const id = (entry.target as HTMLElement).dataset.farmId;
            if (!id) continue;
            if (entry.isIntersecting) {
              if (!visibleWeatherIdsRef.current.has(id)) {
                visibleWeatherIdsRef.current.add(id);
                changed = true;
              }
            } else if (visibleWeatherIdsRef.current.delete(id)) {
              changed = true;
            }
          }
          if (changed) syncViewportWeatherIds();
        },
        {
          root: node,
          // Small margin so cards near the edge still count as visible
          rootMargin: '24px 0px',
          threshold: 0.15,
        }
      );
      weatherObserverRef.current = observer;
      node.querySelectorAll<HTMLElement>('[data-farm-id]').forEach((el) => {
        observer.observe(el);
      });
    },
    [isFarmGroupMode, syncViewportWeatherIds]
  );

  // Re-observe cards when the rendered farm list changes (filters / reload / expand).
  useEffect(() => {
    const root = fieldsScrollRef.current;
    const observer = weatherObserverRef.current;
    if (!root || !observer || isFarmGroupMode) return;

    visibleWeatherIdsRef.current = new Set();
    observer.disconnect();
    root.querySelectorAll<HTMLElement>('[data-farm-id]').forEach((el) => {
      observer.observe(el);
    });
    // IntersectionObserver will fire asynchronously for current intersections
  }, [filteredFields, isFarmGroupMode, showFarmersCatalog]);

  // Fetch OpenWeather only for viewport-visible farm cards (per-farm centroid; cached 10 min).
  useEffect(() => {
    if (isFarmGroupMode || viewportWeatherFarmIds.length === 0) return;
    let cancelled = false;

    const load = async () => {
      const map = await fetchFarmCurrentWeatherMap(viewportWeatherFarmIds, 12);
      if (cancelled) return;
      setFieldWeatherById((prev) => {
        const next = { ...prev };
        // Drop weather for farms no longer in viewport (keeps memory/UI honest)
        for (const id of Object.keys(next)) {
          if (!viewportWeatherFarmIds.includes(id)) delete next[id];
        }
        return { ...next, ...map };
      });
    };

    void load();
    const refreshId = window.setInterval(load, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshId);
    };
  }, [viewportWeatherFarmIds.join('|'), isFarmGroupMode]);

  // Risk covers the whole list, not just the visible page, so filtering spans pagination.
  const riskFarmIds = useMemo(
    () =>
      allFields
        .filter((f) => f?.farm_id != null && !f.isDemo && !f.isUnsaved)
        .map((f) => String(f.farm_id)),
    [allFields]
  );

  useEffect(() => {
    if (riskFarmIds.length === 0) {
      setRiskStatusById({});
      onFarmRiskStatuses?.({});
      return;
    }
    let cancelled = false;
    void fetchFarmRiskStatuses(riskFarmIds).then((byFarmId) => {
      if (cancelled) return;
      setRiskStatusById(byFarmId);
      onFarmRiskStatuses?.(byFarmId);
    });
    return () => {
      cancelled = true;
    };
  }, [riskFarmIds.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDeleteModal = (fieldId: any) => {
    setFieldToDelete(fieldId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setFieldToDelete(null);
    setShowDeleteModal(false);
  };

  const confirmDeleteField = async () => {
    try {
      if (fieldToDelete === 'demo_field') {
        // Handle demo field deletion
        if (onDeleteDemoField) {
          onDeleteDemoField();
        }
      } else {
        // Handle regular field deletion via Digisaka wrapper (ownership enforced server-side)
        await deleteFarmViaDigisakaApi(fieldToDelete);

        // Update local state immediately
        setFields(fields.filter((field: any) => field.farm_id !== fieldToDelete));

        // Notify parent component (clears selection / panel if needed)
        if (onDeleteField) {
          onDeleteField(fieldToDelete);
        }

        // Refresh the fields list to ensure consistency
        fetchFields();
      }
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting field:", error);
      const message = error instanceof Error ? error.message : 'Failed to delete field';
      alert(message);
      closeDeleteModal();
    }
  };

  const handleFieldClick = (field: any) => {
    if (onFieldSelect) {
      // For demo fields, we need to pass the geometry correctly
      if (field.isDemo && field.geometry) {
        // Create a properly formatted field object
        const formattedField = {
          ...field,
          // Ensure the geometry is available for analysis
          _geometry: field.geometry
        };
        onFieldSelect(formattedField);
      } else {
        onFieldSelect(field);
      }
    }
    if (onAnalysisPanelToggle) {
      onAnalysisPanelToggle();
    }
  };

  /** Open analysis for one polygon block of a multi-block farm. */
  const handleBlockClick = (field: any, blockIndex: number, ring: number[][]) => {
    const blockNumber = blockIndex + 1;
    const blockField = {
      ...field,
      polygons: [ring],
      plot_count: 1,
      block_index: blockIndex,
      block_number: blockNumber,
      block_label: displayBlockContextLabel(field as Record<string, unknown>, blockNumber),
      // Keep farm_name for APIs; analysis UI can use block_label when present
      geometry: { type: 'Polygon', coordinates: [ring] },
    };
    setExpandedFarmId(String(field.farm_id));
    handleFieldClick(blockField);
  };

  const handleFarmCardClick = (field: any) => {
    if (isMultiFieldMode) {
      if (multiFieldSelectionLocked) return;
      toggleMultiFieldSelection(field);
      return;
    }

    // Open the whole farm so every block is drawn. A specific block click
    // still goes through handleBlockClick for single-block analysis.
    if (field.farm_id != null) {
      setExpandedFarmId(String(field.farm_id));
    }
    handleFieldClick(field);
  };

  const toggleMultiFieldSelection = (field: any) => {
    if (multiFieldSelectionLocked) return;
    const farmId = field.farm_id;

    setSelectedMultiFieldIds((prevIds) => {
      const exists = prevIds.some((id) => String(id) === String(farmId));
      if (exists) {
        return prevIds.filter((id) => String(id) !== String(farmId));
      }
      if (prevIds.length >= MAX_FARM_GROUP_MEMBERS) {
        return prevIds;
      }
      return [...prevIds, farmId];
    });

    setSelectedMultiFields((prevFields) => {
      const exists = prevFields.some((f) => String(f.farm_id) === String(farmId));
      if (exists) {
        return prevFields.filter((f) => String(f.farm_id) !== String(farmId));
      }
      if (prevFields.length >= MAX_FARM_GROUP_MEMBERS) {
        return prevFields;
      }
      return [...prevFields, field];
    });
  };

  // Check if field is selected
  const isFieldSelected = (field: any) => {
    return String(selectedFieldId ?? '') === String(field.farm_id ?? '');
  };

  // Keep the matching farm expanded when selection comes from the map / analysis
  useEffect(() => {
    if (selectedFieldId == null || selectedFieldId === '') return;
    const id = String(selectedFieldId);
    const field = fields.find((f) => String(f.farm_id) === id);
    if (!field) return;
    const count = fieldBlockCount(field) ?? 0;
    if (count > 1) setExpandedFarmId(id);
  }, [selectedFieldId, selectedField?.block_index, fields]);

  const handleDrawFieldToggle = () => {
    const drawingActive = isDrawingMode || manualDrawEditing;
    const newDrawingMode = !drawingActive;
    if (newDrawingMode && !isFarmLevelMapZoom) return;
    if (onDrawingModeToggle) {
      onDrawingModeToggle(newDrawingMode);
    }
  };

  const drawingUiActive = isDrawingMode || manualDrawEditing || manualDrawRecoverable;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[115]"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar.
          - Below `lg`: fixed drawer that slides in/out (translate-x), controlled by `isOpen`.
          - On `lg+`: when `isOpen` it's a relative flex column that takes its 320px slot in the
            layout; when `!isOpen` it's `display: none` (removed from layout) so the map can
            expand to use the freed space for analysis. A "show sidebar" floating button on the
            map (mounted by `HomePage`) brings it back. */}
      <div
        data-tour-id="sidebar"
        className={`sidebar-shell fixed left-0 top-0 h-full w-80 transform transition-transform duration-300 z-[120] flex flex-col ${
          isDark ? 'bg-[#161b22] text-[#e6edf3] border-r border-[#2d3748]' : 'bg-white text-[#111827] border-r border-[#e5e7eb]'
        } ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${
          // Keep `lg:flex` (not `lg:block`) so flex-1 / min-h-0 scroll regions work on desktop.
          isOpen ? 'lg:relative lg:translate-x-0 lg:flex lg:flex-col lg:h-full' : 'lg:hidden'
        }`}
      >
        
        {/* Mobile drawer chrome only — hide/show on desktop lives in the topbar so this row
            does not sit above the QR card. */}
        <div className={`px-3 py-2.5 border-b lg:hidden ${sb.headerBorder}`}>
          <div className="flex items-center justify-between">
            <img
              src={logo}
              alt="Digisaka"
              className="h-8 w-auto shrink-0 object-contain object-left"
            />
            <button
              onClick={onToggle}
              className={`p-0.5 rounded transition-colors ${sb.headerHover}`}
              aria-label="Close menu"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Field Management Content */}
        <div
          className={`flex-1 min-h-0 p-4 ${
            isFarmListFocusMode ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
          }`}
        >
          {/* Field Tools — BLO / plantation / LGU (LGU saves stay inside the monitoring area). */}
          <div className="mb-4 shrink-0" data-tour-id="field-tools">
            <p className={`mb-2.5 ${isDark ? sb.eyebrowDark : sb.eyebrow}`}>
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#008540]" aria-hidden />
              Field tools
            </p>
            {isLguUser && lguMonitoringLabel && (
              <p
                className={`mb-2 rounded-md border px-2 py-1.5 text-[10px] leading-snug ${
                  isDark
                    ? 'border-emerald-700/50 bg-emerald-950/30 text-emerald-100'
                    : 'border-[#008540]/20 bg-[#008540]/8 text-[#0f5a2e]'
                }`}
              >
                Draw or detect fields inside {lguMonitoringLabel}. You will assign each save to a
                farmer in this monitoring area.
              </p>
            )}
            {!isFarmLevelMapZoom && (
              <p
                className={`mb-2 rounded-md border px-2 py-1.5 text-[10px] leading-snug ${
                  isDark
                    ? 'border-amber-700/60 bg-amber-950/40 text-amber-200'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
              >
                <i className="ri-zoom-in-line mr-1" aria-hidden />
                Zoom in to farm level (zoom {FARM_LEVEL_MIN_ZOOM}+) to draw fields or run AI delineation
                on the map view.
              </p>
            )}
            <div className="space-y-2">
              <div className="relative">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-tour-id="tool-draw-field"
                    onClick={handleDrawFieldToggle}
                    disabled={!drawingUiActive && !isFarmLevelMapZoom}
                    title={
                      drawingUiActive
                        ? 'Stop drawing'
                        : !isFarmLevelMapZoom
                          ? farmLevelZoomHint
                          : 'Draw new field — trace a boundary by hand'
                    }
                    className={`flex items-center gap-2 rounded-[12px] px-2.5 py-2 text-left transition-colors duration-150 min-h-[44px] ${
                      drawingUiActive ? sb.toolCardActive : sb.toolCard
                    } ${!drawingUiActive && !isFarmLevelMapZoom ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${
                        drawingUiActive ? 'bg-white/16 text-white' : sb.toolIconWrap
                      }`}
                    >
                      <i className={`${drawingUiActive ? 'ri-stop-line' : 'ri-pencil-line'} text-sm`} />
                    </span>
                    <span className="min-w-0 flex-1 text-[12px] font-semibold leading-tight">
                      {drawingUiActive ? 'Stop drawing' : 'Draw field'}
                    </span>
                  </button>

                  {aiFieldDelineationStep === 'idle' && !drawingUiActive ? (
                    <button
                      type="button"
                      data-tour-id="tool-ai-delineation"
                      onClick={() => {
                        if (!isFarmLevelMapZoom) return;
                        setShowFileUploadMenu(false);
                        setUploadFormatHelpId(null);
                        onAiFieldDelineationOpen?.();
                      }}
                      disabled={!isFarmLevelMapZoom}
                      title={
                        !isFarmLevelMapZoom
                          ? farmLevelZoomHint
                          : 'AI field delineation — auto-detect boundaries'
                      }
                      className={`flex items-center gap-2 rounded-[12px] px-2.5 py-2 text-left transition-colors duration-150 min-h-[44px] ${
                        aiFieldDelineationStep !== 'idle' ? sb.toolCardActive : sb.toolCard
                      } ${!isFarmLevelMapZoom ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${
                          aiFieldDelineationStep !== 'idle' ? 'bg-white/16 text-white' : sb.toolIconWrap
                        }`}
                      >
                        <i className="ri-focus-3-line text-sm" />
                      </span>
                      <span className="min-w-0 flex-1 text-[12px] font-semibold leading-tight">
                        AI delineation
                      </span>
                    </button>
                  ) : (
                    <div
                      className={`flex items-center gap-2 rounded-[12px] px-2.5 py-2 min-h-[44px] opacity-40 ${sb.toolCard}`}
                      aria-hidden
                      title="AI delineation in progress"
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${sb.toolIconWrap}`}>
                        <i className="ri-focus-3-line text-sm" />
                      </span>
                      <span className="min-w-0 flex-1 text-[12px] font-semibold leading-tight">
                        AI delineation
                      </span>
                    </div>
                  )}

                  <button
                    type="button"
                    data-tour-id="tool-upload-field"
                    onClick={() => {
                      if (isUploadingFieldFile) return;
                      setShowFileUploadMenu((open) => {
                        if (open) setUploadFormatHelpId(null);
                        return !open;
                      });
                    }}
                    disabled={isUploadingFieldFile}
                    title="Upload field file"
                    className={`flex items-center gap-2 rounded-[12px] px-2.5 py-2 text-left transition-colors duration-150 min-h-[44px] border border-dashed disabled:opacity-60 ${
                      showFileUploadMenu
                        ? sb.toolCardActive
                        : isDark
                          ? 'border-[#2d3748] text-[#8b949e] hover:border-white/12'
                          : 'border-[#e5e7eb] text-[#374151] hover:border-[#d1d5db]'
                    }`}
                    aria-expanded={showFileUploadMenu}
                    aria-haspopup="menu"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${
                        showFileUploadMenu ? 'bg-white/16 text-white' : sb.toolIconWrap
                      }`}
                    >
                      {isUploadingFieldFile ? (
                        <i className="ri-loader-4-line text-sm animate-spin" aria-hidden />
                      ) : (
                        <i className="ri-upload-line text-sm" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1 text-[12px] font-semibold leading-tight">
                      {isUploadingFieldFile ? 'Uploading…' : 'Upload file'}
                    </span>
                  </button>

                  {!isLguUser && (
                  <button
                    type="button"
                    onClick={() => {
                      if (multiFieldSelectionLocked) return;
                      if (isFarmGroupMode) {
                        exitFarmGroupMode();
                      } else {
                        enterFarmGroupMode();
                      }
                    }}
                    disabled={multiFieldSelectionLocked}
                    title={
                      isFarmGroupMode ? 'Exit group farms' : 'Group farms — manage farms as one set'
                    }
                    className={`flex items-center gap-2 rounded-[12px] px-2.5 py-2 text-left transition-colors duration-150 min-h-[44px] ${
                      isFarmGroupMode ? sb.toolCardActive : sb.toolCard
                    } ${multiFieldSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${
                        isFarmGroupMode ? 'bg-white/16 text-white' : sb.toolIconWrap
                      }`}
                    >
                      <i className="ri-layout-grid-line text-sm" />
                    </span>
                    <span className="min-w-0 flex-1 text-[12px] font-semibold leading-tight">
                      {isFarmGroupMode ? 'Exit group' : 'Group farms'}
                    </span>
                  </button>
                  )}
                </div>

                {showFileUploadMenu && (
                  <div
                    className={`absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border shadow-lg ${sb.menuDropdown}`}
                  >
                    {FIELD_UPLOAD_FORMATS.map((format, index) => {
                      const helpOpen = uploadFormatHelpId === format.id;
                      const accentClass =
                        format.accent === 'red'
                          ? 'text-red-500'
                          : format.accent === 'purple'
                            ? 'text-purple-500'
                            : isDark
                              ? 'text-[#34d399]'
                              : 'text-[#008540]';
                      const isLast = index === FIELD_UPLOAD_FORMATS.length - 1;
                      return (
                        <div
                          key={format.id}
                          className={`${index > 0 ? `border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}` : ''} ${isLast ? 'rounded-b-lg' : ''}`}
                        >
                          <div className={`flex items-center ${sb.menuItem}`}>
                            <label className="min-w-0 flex-1 cursor-pointer px-4 py-2">
                              <div className="flex items-center space-x-2">
                                <i className={`${format.icon} ${accentClass}`} aria-hidden />
                                <span className="text-sm font-medium">{format.label}</span>
                              </div>
                              <input
                                type="file"
                                style={{ display: 'none' }}
                                multiple={Boolean(format.multiple)}
                                accept={format.accept}
                                onChange={(e) => handleFileChange(e, format.id)}
                              />
                            </label>
                            <button
                              type="button"
                              className={`mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                                helpOpen
                                  ? isDark
                                    ? 'bg-white/[0.08] text-[#34d399]'
                                    : 'bg-[#f3f4f6] text-[#008540]'
                                  : isDark
                                    ? 'text-[#8b949e] hover:bg-white/[0.06] hover:text-[#e6edf3]'
                                    : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#008540]'
                              }`}
                              aria-label={`About ${format.label}`}
                              aria-expanded={helpOpen}
                              title={`About ${format.label}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setUploadFormatHelpId((current) =>
                                  current === format.id ? null : format.id
                                );
                              }}
                            >
                              <i className="ri-information-line text-sm" aria-hidden />
                            </button>
                          </div>
                          {helpOpen && (
                            <p className={`px-4 pb-2 text-[11px] leading-snug ${sb.menuItemMuted}`}>
                              {format.help}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {isDrawingMode && !manualDrawEditing && !manualDrawRecoverable && (
                <div className={sb.drawBanner}>
                  <div className={sb.drawBannerTitle}>
                    <i className="ri-information-line mr-1"></i>
                    Drawing Mode Active
                  </div>
                  <p className={sb.drawBannerText}>
                    Click on the map to place corners. Double-click to close a block — then draw
                    another block, or adjust vertices before confirming.
                  </p>
                </div>
              )}

              {manualDrawEditing && (
                <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                  <div className={`${sb.panelTitle} text-xs`}>
                    {isUpdatingMode ? 'Edit field blocks' : 'Adjust blocks'}
                  </div>
                  <p className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {isUpdatingMode
                      ? 'Draw another block, drag vertices to reshape, or remove a block. Confirm when ready.'
                      : 'Keep drawing more blocks, drag vertices to fix mis-clicks, or remove a block. Confirm when the outline looks correct.'}
                  </p>
                  <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Tip: Shift+click a block to select it before Delete.
                  </p>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => onRedrawManualDraw?.()}
                      className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors ${sb.secondaryBtn}`}
                    >
                      Redraw all
                    </button>
                    <button
                      type="button"
                      onClick={() => onCancelManualDrawEdit?.()}
                      className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors ${sb.secondaryBtn}`}
                    >
                      Pause
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeleteManualDrawBlock?.()}
                    className={`w-full rounded-lg py-1.5 text-xs font-medium transition-colors ${sb.secondaryBtn}`}
                  >
                    <i className="ri-delete-bin-line mr-1" aria-hidden />
                    Delete block
                  </button>
                  <button
                    type="button"
                    onClick={() => onConfirmManualDraw?.()}
                    className={`w-full rounded-lg py-1.5 text-xs font-medium transition-all duration-300 ${sb.primaryBtn}`}
                  >
                    {isUpdatingMode ? 'Update the field' : 'Use as my field'}
                  </button>
                </div>
              )}

              {manualDrawRecoverable && !manualDrawEditing && (
                <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                  <div className={`${sb.panelTitle} text-xs`}>Boundary on map</div>
                  <p className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {isUpdatingMode
                      ? 'Blocks are still visible. Edit them, save the update, or discard.'
                      : 'Drawing was stopped but your blocks are still visible. Edit, save as your field, or discard.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => onResumeManualDrawEdit?.()}
                    className={`w-full rounded px-3 py-1.5 text-xs font-medium transition-colors ${sb.secondaryBtn}`}
                  >
                    <i className="ri-edit-line mr-1" aria-hidden />
                    Edit blocks
                  </button>
                  <button
                    type="button"
                    onClick={() => onConfirmManualDraw?.()}
                    className={`w-full rounded-lg py-1.5 text-xs font-medium transition-all duration-300 ${sb.primaryBtn}`}
                  >
                    {isUpdatingMode ? 'Update the field' : 'Use as my field'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDiscardManualDraw?.()}
                    className={`w-full rounded-lg py-1.5 text-xs font-medium transition-colors ${sb.secondaryBtn}`}
                  >
                    Discard
                  </button>
                </div>
              )}

              {/* AI Field Delineation — expanded panels (full width below Draw row) */}
              <div className="space-y-1.5">
                {aiFieldDelineationStep === 'instruction' && (
                  <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                    <div className="flex items-start justify-between gap-1.5">
                      <div className={`flex items-center text-xs leading-tight ${sb.panelTitle}`}>
                        <i className="ri-landscape-ai-line mr-1 text-sm shrink-0"></i>
                        AI field delineation
                      </div>
                      <button
                        type="button"
                        onClick={() => onAiFieldDelineationClose?.()}
                        className={`text-[11px] shrink-0 ${sb.panelMuted}`}
                      >
                        Cancel
                      </button>
                    </div>

                    <div className={sb.segmentWrap}>
                      <button
                        type="button"
                        disabled={aiFieldDelineationLoading}
                        onClick={() => onAiFieldDelineationInputModeChange?.('mapView')}
                        className={`flex-1 py-1 px-1.5 text-[11px] font-medium transition-colors ${
                          aiFieldDelineationInputMode === 'mapView'
                            ? sb.segmentActive
                            : sb.segmentInactive
                        }`}
                      >
                        Map view
                      </button>
                      <button
                        type="button"
                        disabled={aiFieldDelineationLoading}
                        onClick={() => onAiFieldDelineationInputModeChange?.('file')}
                        className={`flex-1 py-1 px-1.5 text-[11px] font-medium transition-colors ${
                          aiFieldDelineationInputMode === 'file'
                            ? sb.segmentActive
                            : sb.segmentInactive
                        }`}
                      >
                        GeoTIFF file
                      </button>
                    </div>

                    {aiFieldDelineationInputMode === 'mapView' ? (
                      <div className="space-y-1.5">
                        <p className={`text-[11px] leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Pan and zoom the map until your farm area fills the screen (zoom{' '}
                          {FARM_LEVEL_MIN_ZOOM}+), then tap{' '}
                          <span className={sb.panelTextStrong}>Process</span>. Field boundaries will be
                          detected for the current map view.
                        </p>
                        {!isFarmLevelMapZoom && (
                          <p className="text-[11px] leading-relaxed text-amber-500">
                            <i className="ri-error-warning-line mr-1" aria-hidden />
                            {farmLevelZoomHint}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <p className={`text-[11px] leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Choose a georeferenced <span className={sb.panelTextStrong}>GeoTIFF</span>, then
                          tap <span className={sb.panelTextStrong}>Process</span> to detect field
                          boundaries from the raster.
                        </p>
                        <label className="block">
                          <span className="sr-only">GeoTIFF file</span>
                          <input
                            type="file"
                            accept=".tif,.tiff,.TIF,.TIFF"
                            disabled={aiFieldDelineationLoading}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              onAiFieldDelineationGeoTiffChange?.(f ?? null);
                            }}
                            className={`block w-full text-[10px] file:mr-1.5 file:rounded-[8px] file:border-0 file:text-[10px] file:font-medium file:px-1.5 file:py-1 file:text-white hover:file:opacity-90 ${
                              isDark ? 'text-[#c9d1d9] file:bg-[#008540] hover:file:bg-[#097a34]' : 'text-[#374151] file:bg-[#008540] hover:file:bg-[#008540CC]'
                            }`}
                          />
                        </label>
                        {aiFieldDelineationGeoTiffName && (
                          <p
                            className={`truncate text-[10px] ${sb.countText}`}
                            title={aiFieldDelineationGeoTiffName}
                          >
                            Selected: {aiFieldDelineationGeoTiffName}
                          </p>
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      disabled={
                        aiFieldDelineationLoading ||
                        (aiFieldDelineationInputMode === 'file' && !aiFieldDelineationGeoTiffName) ||
                        (aiFieldDelineationInputMode === 'mapView' && !isFarmLevelMapZoom)
                      }
                      title={
                        aiFieldDelineationInputMode === 'mapView' && !isFarmLevelMapZoom
                          ? farmLevelZoomHint
                          : undefined
                      }
                      onClick={() => onAiFieldDelineationProcess?.()}
                      className={`flex w-full items-center justify-center rounded px-3 py-1.5 text-xs font-medium transition-all duration-300 ${sb.primaryBtn}`}
                    >
                      {aiFieldDelineationLoading ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border border-white border-t-transparent" />
                          Processing…
                        </span>
                      ) : aiFieldDelineationInputMode === 'file' ? (
                        <>
                          <i className="ri-file-image-line mr-1.5 text-sm"></i>
                          Process GeoTIFF
                        </>
                      ) : (
                        <>
                          <i className="ri-map-pin-line mr-1.5 text-sm"></i>
                          Process map view
                        </>
                      )}
                    </button>
                    {aiFieldDelineationError && (
                      <p className="text-[11px] text-red-400">{aiFieldDelineationError}</p>
                    )}
                  </div>
                )}

                {aiFieldDelineationStep === 'picking' && (
                  <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                    <div className="flex items-start justify-between gap-1.5">
                      <div className={`${sb.panelTitle} text-xs leading-tight`}>
                        {aiFieldDelineationOrigin === 'upload'
                          ? `Choose polygons (${aiFieldDelineationFieldCount} in file)`
                          : `Choose fields (${aiFieldDelineationFieldCount} detected)`}
                      </div>
                      <button
                        type="button"
                        onClick={() => onAiFieldDelineationClose?.()}
                        className={`text-[11px] shrink-0 ${sb.panelMuted}`}
                      >
                        Close
                      </button>
                    </div>
                    <p className={`text-[11px] leading-snug ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {aiDelineationMultiSelect
                        ? `Tick blocks, or ${modHint}+click / Shift+click on the map. Click empty map or Clear to deselect. Save in batches — the rest stay.`
                        : `Click a block to select; click again or empty map to deselect. ${modHint}+click adds to multi-select.`}
                    </p>
                    <label className={`flex items-center gap-2 text-[10px] cursor-pointer ${sb.panelMuted}`}>
                      <input
                        type="checkbox"
                        className="rounded border-gray-400"
                        checked={showSavedFieldsDuringDelineation}
                        onChange={(e) => onShowSavedFieldsDuringDelineationChange?.(e.target.checked)}
                      />
                      Show saved fields on map (off by default to reduce overlap)
                    </label>
                    {aiFieldDelineationFieldCount > 0 && (
                      <button
                        type="button"
                        onClick={() => onDismissOverlappingSavedDelineations?.()}
                        className={`w-full rounded px-2 py-1 text-[10px] font-medium ${sb.secondaryBtn}`}
                      >
                        Remove detections over saved fields
                      </button>
                    )}
                    {aiFieldDelineationFieldCount > 1 && (
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => onToggleDelineationMultiSelect?.()}
                          className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                            aiDelineationMultiSelect ? sb.segmentActive : sb.secondaryBtn
                          }`}
                        >
                          {aiDelineationMultiSelect ? 'Multi-select on' : 'Multi-select off'}
                        </button>
                        {aiDelineationMultiSelect && (
                          <>
                            <button
                              type="button"
                              onClick={() => onSelectAllDelineationBlocks?.()}
                              className={`rounded px-2 py-1 text-[10px] font-medium ${sb.secondaryBtn}`}
                            >
                              Select all
                            </button>
                            <button
                              type="button"
                              disabled={checkedCount === 0 && aiDelineationSelectedIndex === null}
                              onClick={() => onClearDelineationSelection?.()}
                              className={`rounded px-2 py-1 text-[10px] font-medium disabled:opacity-50 ${sb.secondaryBtn}`}
                            >
                              Clear
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    <div className="max-h-40 space-y-1 overflow-y-auto overscroll-contain">
                      {Array.from({ length: aiFieldDelineationFieldCount }, (_, i) => {
                        const checked = aiDelineationCheckedIndexes.includes(i);
                        const selected = aiDelineationSelectedIndex === i;
                        return (
                          <div
                            key={i}
                            className={`flex items-center gap-1 rounded px-1 py-0.5 ${
                              aiDelineationMultiSelect
                                ? checked
                                  ? sb.segmentActive
                                  : sb.segmentInactive
                                : selected
                                  ? sb.segmentActive
                                  : sb.segmentInactive
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => onSelectDelineationField?.(i)}
                              className="min-w-0 flex-1 rounded px-1 py-1 text-left text-[11px] font-medium transition-colors flex items-center gap-1.5"
                            >
                              {aiDelineationMultiSelect && (
                                <span
                                  className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[9px] ${
                                    checked
                                      ? isDark
                                        ? 'border-blue-400 bg-blue-500/30 text-blue-100'
                                        : 'border-[#3B82F6] bg-[#3B82F6]/15 text-[#3B82F6]'
                                      : isDark
                                        ? 'border-gray-500 text-transparent'
                                        : 'border-gray-300 text-transparent'
                                  }`}
                                  aria-hidden
                                >
                                  {checked ? '✓' : '·'}
                                </span>
                              )}
                              <span className="min-w-0 truncate">
                                {aiFieldDelineationFieldLabels[i] || String(i + 1)}
                                {!aiDelineationMultiSelect && selected ? ' (selected)' : ''}
                                {aiDelineationMultiSelect && checked ? ' (checked)' : ''}
                              </span>
                            </button>
                            <button
                              type="button"
                              title="Remove this block"
                              aria-label={`Remove ${aiFieldDelineationFieldLabels[i] || String(i + 1)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveDelineationBlock?.(i);
                              }}
                              className={`shrink-0 inline-flex h-6 w-6 items-center justify-center rounded transition-colors ${
                                isDark
                                  ? 'text-red-300/80 hover:bg-red-500/20 hover:text-red-200'
                                  : 'text-red-600/80 hover:bg-red-50 hover:text-red-700'
                              }`}
                            >
                              <i className="ri-delete-bin-line text-sm" aria-hidden />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {aiFieldDelineationFieldCount > 1 && !aiDelineationMultiSelect && (
                      <button
                        type="button"
                        onClick={() => onSaveAllDelineationAsOneField?.()}
                        className={`w-full rounded px-3 py-1.5 text-xs font-medium transition-all duration-300 ${sb.primaryBtn}`}
                      >
                        Save all as one field ({aiFieldDelineationFieldCount} blocks)
                      </button>
                    )}

                    {aiDelineationMultiSelect ? (
                      <div className="space-y-1.5">
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            disabled={checkedCount === 0}
                            onClick={() => onSaveCheckedDelineationAsOneField?.()}
                            className={`rounded px-2 py-1.5 text-xs font-medium transition-all duration-300 disabled:opacity-50 ${sb.primaryBtn}`}
                          >
                            Save selected as one field
                            {checkedCount > 0 ? ` (${checkedCount})` : ''}
                          </button>
                          <button
                            type="button"
                            disabled={checkedCount !== 1}
                            onClick={() => onUseCheckedDelineationField?.()}
                            className={`rounded px-2 py-1.5 text-xs font-medium transition-all duration-300 disabled:opacity-50 ${sb.secondaryBtn}`}
                          >
                            Use 1 selected block
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            disabled={checkedCount === 0}
                            onClick={() => onRemoveCheckedDelineationBlocks?.()}
                            className={`rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                              isDark
                                ? 'text-red-300/90 hover:bg-red-500/15'
                                : 'text-red-700 hover:bg-red-50'
                            }`}
                          >
                            Delete selected{checkedCount > 0 ? ` (${checkedCount})` : ''}
                          </button>
                          <button
                            type="button"
                            disabled={checkedCount !== 1 && aiDelineationSelectedIndex === null}
                            onClick={() => onStartAdjustDelineationVertices?.()}
                            className={`rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                          >
                            Adjust vertices
                          </button>
                        </div>
                        <p className={`text-[10px] ${sb.panelMuted}`}>
                          Saved blocks are removed from the list; unsaved blocks remain for the next batch.
                          Press Delete to remove selected blocks.
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          disabled={aiDelineationSelectedIndex === null}
                          onClick={() => onUseSelectedDelineationField?.()}
                          className={`rounded px-2 py-1.5 text-xs font-medium transition-all duration-300 disabled:opacity-50 ${sb.primaryBtn}`}
                        >
                          Use as my field
                        </button>
                        <button
                          type="button"
                          disabled={aiDelineationSelectedIndex === null}
                          onClick={() => onRemoveDelineationBlock?.()}
                          className={`rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                        >
                          Delete block
                        </button>
                        <button
                          type="button"
                          disabled={aiDelineationSelectedIndex === null}
                          onClick={() => onStartAdjustDelineationVertices?.()}
                          className={`col-span-2 rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                        >
                          Adjust boundary (vertices)
                        </button>
                      </div>
                    )}
                    {aiFieldDelineationError && (
                      <p className="text-[11px] text-red-400">{aiFieldDelineationError}</p>
                    )}
                  </div>
                )}

                {aiFieldDelineationStep === 'editing' && (
                  <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                    <div className={`${sb.panelTitle} text-xs`}>Adjust boundary</div>
                    <p className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Drag vertices to adjust this detected field. Other detections stay visible so
                      you can stay inside them. Done keeps it in the list — then save when ready.
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        disabled={!vertexEditCanUndo}
                        onClick={() => onUndoVertexEdit?.()}
                        className={`rounded-lg py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                        title="Undo last vertex change (Ctrl/⌘+Z)"
                      >
                        Undo
                      </button>
                      <button
                        type="button"
                        disabled={!vertexEditCanRedo}
                        onClick={() => onRedoVertexEdit?.()}
                        className={`rounded-lg py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                        title="Redo last undone change (Ctrl/⌘+Shift+Z)"
                      >
                        Redo
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => onConfirmDelineationAsField?.()}
                      className={`flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition-all duration-300 ${sb.primaryBtn}`}
                    >
                      <i className="ri-check-line text-sm" aria-hidden />
                      Done
                    </button>
                  </div>
                )}

                {farmSplitReview && farmSplitReview.step === 'editing' && (
                  <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                    <div className={`${sb.panelTitle} text-xs`}>Adjust subplot boundary</div>
                    <p className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Drag vertices to adjust this subplot. Other pieces stay visible so you can
                      stay inside them. Use Undo if a drag goes too far.
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        disabled={isSavingFarmSplit || !vertexEditCanUndo}
                        onClick={() => onUndoVertexEdit?.()}
                        className={`rounded-lg py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                        title="Undo last vertex change (Ctrl/⌘+Z)"
                      >
                        Undo
                      </button>
                      <button
                        type="button"
                        disabled={isSavingFarmSplit || !vertexEditCanRedo}
                        onClick={() => onRedoVertexEdit?.()}
                        className={`rounded-lg py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                        title="Redo last undone change (Ctrl/⌘+Shift+Z)"
                      >
                        Redo
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={isSavingFarmSplit}
                      onClick={() => onConfirmSplitVertexEdit?.()}
                      className={`flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition-all duration-300 disabled:opacity-50 ${sb.primaryBtn}`}
                    >
                      <i className="ri-check-line text-sm" aria-hidden />
                      Done
                    </button>
                  </div>
                )}

                {farmSplitReview && farmSplitReview.step !== 'editing' && (
                  <div className={`${sb.panel} p-1.5 space-y-1.5`}>
                    <div className={`${sb.panelTitle} text-xs leading-tight`}>
                      Split review ({farmSplitReview.featureCount} subplot
                      {farmSplitReview.featureCount === 1 ? '' : 's'})
                    </div>
                    <p className={`text-[11px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      Click a piece on the map or select below. Adjust vertices if needed, then save
                      as additional blocks on{' '}
                      <span className="font-medium">{farmSplitReview.baseName}</span>
                      {' '}(Block 1, Block 2, …).
                    </p>
                    <div className="max-h-28 space-y-1 overflow-y-auto">
                      {Array.from({ length: farmSplitReview.featureCount }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          disabled={isSavingFarmSplit}
                          onClick={() => onSelectSplitSubplot?.(i)}
                          className={`w-full rounded px-1.5 py-1 text-left text-[11px] font-medium transition-colors disabled:opacity-50 ${
                            farmSplitReview.selectedIndex === i
                              ? sb.segmentActive
                              : sb.segmentInactive
                          }`}
                        >
                          New block {i + 1}
                          {farmSplitReview.selectedIndex === i ? ' (selected)' : ''}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      disabled={isSavingFarmSplit || farmSplitReview.selectedIndex == null}
                      onClick={() => onStartAdjustSplitVertices?.()}
                      className={`w-full rounded px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                    >
                      Adjust boundary (vertices)
                    </button>
                    <button
                      type="button"
                      disabled={isSavingFarmSplit || farmSplitReview.selectedIndex == null}
                      onClick={() => onSaveSelectedSplitSubplot?.()}
                      className={`w-full rounded px-3 py-1.5 text-xs font-medium transition-all duration-300 disabled:opacity-50 ${sb.primaryBtn}`}
                    >
                      {isSavingFarmSplit ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-1" aria-hidden />
                          Saving…
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line mr-1" aria-hidden />
                          Save as block
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={isSavingFarmSplit || farmSplitReview.selectedIndex == null}
                      onClick={() => onRemoveSplitSubplot?.()}
                      className={`w-full rounded px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                    >
                      Remove selected
                    </button>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        disabled={isSavingFarmSplit}
                        onClick={() => onCancelFarmSplit?.()}
                        className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${sb.secondaryBtn}`}
                      >
                        Discard
                      </button>
                      <button
                        type="button"
                        disabled={isSavingFarmSplit || farmSplitReview.featureCount < 1}
                        onClick={() => onSaveFarmSplit?.()}
                        className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-all duration-300 disabled:opacity-50 ${sb.primaryBtn}`}
                      >
                        {isSavingFarmSplit ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-1" aria-hidden />
                            Saving…
                          </>
                        ) : (
                          <>
                            <i className="ri-save-3-line mr-1" aria-hidden />
                            Save all as blocks
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Group mode status — Group farms button lives in Field tools 2×2 grid */}
          {!isLguUser && isFarmGroupMode && (
          <div className="mb-2 shrink-0">
            <p className={`text-xs text-center ${sb.countText}`}>
              {multiFieldSelectionLocked
                ? 'Processing analysis — field selection temporarily locked'
                : farmGroupView === 'groups'
                  ? 'Saved groups only — open a group or start a new selection'
                  : isOpenedGroupFocus
                    ? `Only group farms — click a farm to analyze, or use Multiple Field Analysis`
                    : selectedMultiFields.length === 0
                      ? `Select farms (up to ${MAX_FARM_GROUP_MEMBERS}) — filters keep your selection`
                      : `${selectedMultiFields.length} / ${MAX_FARM_GROUP_MEMBERS} selected`}
            </p>
          </div>
          )}

          {/* Groups view: hide individual Saved Fields */}
          {isFarmGroupMode && farmGroupView === 'groups' ? (
            <div className={`relative mb-3 ${isFarmListFocusMode ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
              <div className="flex items-center justify-between mb-3 shrink-0">
                <h4 className={sb.sectionTitle}>My farm groups</h4>
                <span className={`text-xs ${isDark ? sb.countText : 'text-[#008540] font-semibold'}`}>
                  ({farmGroups.length})
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (multiFieldSelectionLocked) return;
                  setSelectedMultiFieldIds([]);
                  setSelectedMultiFields([]);
                  clearOpenedFarmGroup();
                  setFarmGroupView('pick');
                  onClearFarmGroupMap?.();
                }}
                disabled={multiFieldSelectionLocked}
                className={`w-full mb-3 shrink-0 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${sb.fullBtn} ${
                  multiFieldSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <i className={`ri-add-line text-sm ${isDark ? '' : 'text-[#008540]'}`} />
                <span>New group — select farms</span>
              </button>

              {farmGroupsError && (
                <p className={`mb-2 shrink-0 text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`}>{farmGroupsError}</p>
              )}

              {farmGroupsLoading ? (
                <ApiListSkeleton count={4} label="Loading farm groups" />
              ) : farmGroups.length === 0 ? (
                <div className={`text-center py-6 text-sm ${sb.emptyText}`}>
                  No saved groups yet. Select farms across provinces, then save a group for later analysis.
                </div>
              ) : (
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pb-4">
                  {farmGroups.map((group) => {
                    const busy = groupActionBusyId === group.id;
                    return (
                      <div
                        key={group.id}
                        className={`rounded-lg p-2.5 ${sb.fieldCard}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium truncate ${sb.fieldName}`}>
                              {group.name}
                            </div>
                            <div className={`text-xs mt-0.5 ${sb.fieldSub}`}>
                              {group.member_count} farm{group.member_count === 1 ? '' : 's'}
                              {group.description ? ` · ${group.description}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              disabled={busy || multiFieldSelectionLocked}
                              onClick={() => void handleOpenGroup(group.id)}
                              className={`text-xs px-2 py-1 rounded-[8px] font-medium ${sb.primaryBtn} ${busy ? 'opacity-60' : ''}`}
                            >
                              {busy ? '…' : 'Open'}
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void handleDeleteGroup(group.id)}
                              className="text-xs px-2 py-1 rounded text-red-500 hover:bg-red-500/10"
                              title="Delete group"
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
          // Saved Fields Section (hidden while browsing farm groups)
          <div
            className={`relative ${
              isFarmListFocusMode ? 'flex min-h-0 flex-1 flex-col' : ''
            }`}
          >

            {isLguUser && !isFarmGroupMode && !isOpenedGroupFocus && (
              <div
                className={`mb-2 grid grid-cols-2 gap-1 rounded-lg p-0.5 ${
                  isDark ? 'bg-white/5' : 'bg-[#f3f4f6]'
                } ${isFarmListFocusMode ? 'shrink-0' : ''}`}
                role="tablist"
                aria-label="Monitoring catalog"
              >
                {(
                  [
                    { id: 'fields' as const, label: 'Fields', icon: 'ri-landscape-line' },
                    { id: 'farmers' as const, label: 'Farmers', icon: 'ri-user-line' },
                  ] as const
                ).map((tab) => {
                  const active = lguCatalogTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => {
                        setLguCatalogTab(tab.id);
                        setSearchQuery('');
                      }}
                      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                        active
                          ? isDark
                            ? 'bg-emerald-600/90 text-white'
                            : 'bg-[#008540] text-white'
                          : isDark
                            ? 'text-gray-300 hover:bg-white/5'
                            : 'text-[#3d5c47] hover:bg-white/70'
                      }`}
                    >
                      <i className={`${tab.icon} text-sm`} aria-hidden />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={`flex items-center justify-between mb-3 ${isFarmListFocusMode ? 'shrink-0' : ''}`}>
              <p className={isDark ? sb.eyebrowDark : sb.eyebrow}>
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#008540]" aria-hidden />
                {isOpenedGroupFocus
                  ? openedFarmGroup?.name || 'Group farms'
                  : isFarmGroupMode
                    ? 'Select farms for group'
                    : showFarmersCatalog
                      ? 'Farmers'
                      : 'Saved fields'}
              </p>
              <div className="flex items-center gap-2">
                {isLguUser && lguMonitoringLabel && (
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                      isDark ? 'bg-white/10 text-emerald-200' : 'bg-[#008540]/10 text-[#008540]'
                    }`}
                    title="Monitoring area from Digisaka registration"
                  >
                    {lguMonitoringLabel}
                  </span>
                )}
                {isFarmGroupMode && (
                  <button
                    type="button"
                    onClick={() => {
                      clearOpenedFarmGroup();
                      setSelectedMultiFieldIds([]);
                      setSelectedMultiFields([]);
                      setFarmGroupView('groups');
                      onClearFarmGroupMap?.();
                    }}
                    className={`text-xs cursor-pointer whitespace-nowrap font-medium ${sb.linkText}`}
                  >
                    My groups
                  </button>
                )}
                <span className={sb.countPill} title={showFarmersCatalog ? undefined : 'Total saved farms'}>
                  {fieldsCountLabel}
                </span>
              </div>
            </div>

            {isLguUser && !showFarmersCatalog && farmerFilter && (
              <div
                className={`mb-2 flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                  isFarmListFocusMode ? 'shrink-0' : ''
                } ${
                  isDark
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
                    : 'border-[#008540]/20 bg-[#008540]/8 text-[#008540]'
                }`}
              >
                <i className="ri-user-line text-sm shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                  Fields of {farmerFilter.name}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setFarmerFilter(null);
                  }}
                  className="shrink-0 text-[10px] font-semibold underline-offset-2 hover:underline"
                >
                  Clear
                </button>
              </div>
            )}

            {isFarmGroupMode &&
              (isOpenedGroupFocus
                ? openedGroupMembers.length >= 1
                : selectedMultiFields.length >= 1) && (
              <div
                className={`mb-3 rounded-lg border p-2 space-y-2 ${
                  isFarmListFocusMode ? 'shrink-0' : ''
                } ${sb.infoBanner}`}
              >
                <p className={`text-xs text-center ${sb.countText}`}>
                  {isOpenedGroupFocus
                    ? `${openedGroupMembers.length} farm${openedGroupMembers.length === 1 ? '' : 's'} — click one for farm analysis, or run Multiple Field Analysis`
                    : selectedMultiFields.length >= MAX_FARM_GROUP_MEMBERS
                      ? `${selectedMultiFields.length} / ${MAX_FARM_GROUP_MEMBERS} selected — maximum reached`
                      : `${selectedMultiFields.length} / ${MAX_FARM_GROUP_MEMBERS} selected — change filters to add more`}
                </p>
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    disabled={
                      (isOpenedGroupFocus
                        ? openedGroupMembers.length < 2
                        : selectedMultiFields.length < 2) || multiFieldSelectionLocked
                    }
                    onClick={handleStartMultiFieldFromSelection}
                    className={`w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${
                      (isOpenedGroupFocus
                        ? openedGroupMembers.length < 2
                        : selectedMultiFields.length < 2) || multiFieldSelectionLocked
                        ? 'opacity-50 cursor-not-allowed ' + sb.fullBtn
                        : sb.fullBtnActive
                    }`}
                  >
                    <i className="ri-line-chart-line text-sm" />
                    <span>Multiple Field Analysis</span>
                  </button>
                  {!isOpenedGroupFocus && (
                    <button
                      type="button"
                      disabled={multiFieldSelectionLocked || saveGroupBusy}
                      onClick={openSaveGroupModal}
                      className={`w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${sb.fullBtn}`}
                    >
                      <i className={`ri-save-line text-sm ${isDark ? '' : 'text-[#008540]'}`} />
                      <span>Save group</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Field filters — row 1: search + date; row 2: municipality + risk (BLO) / risk (LGU). */}
            {!drawnField && (
              <div className={`mb-3 space-y-1.5 ${isFarmListFocusMode ? 'shrink-0' : ''}`}>
                <div
                  className={`grid gap-1.5 ${
                    !isOpenedGroupFocus && !showFarmersCatalog
                      ? 'grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]'
                      : 'grid-cols-1'
                  }`}
                >
                  <div className="relative min-w-0">
                    <i
                      className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"
                      aria-hidden
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                      placeholder={showFarmersCatalog ? 'Search farmer' : 'Search Farm'}
                      aria-label={
                        showFarmersCatalog
                          ? 'Search farmers by name or ID'
                          : 'Search saved fields by name or ID'
                      }
                      className={`${sb.input} pl-7 pr-7`}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        aria-label="Clear field search"
                        className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 ${sb.countText} hover:opacity-80`}
                      >
                        <i className="ri-close-line text-xs" aria-hidden />
                      </button>
                    )}
                  </div>

                  {!isOpenedGroupFocus && !showFarmersCatalog && (
                    <div className="relative min-w-0">
                      <select
                        aria-label="Filter by date added"
                        value={dateFilter || ''}
                        onChange={(e) => {
                          setDateFilter(e.target.value || null);
                        }}
                        className={`${sb.input} px-1.5 pr-6 appearance-none truncate`}
                      >
                        {dateFilterOptions.map((option) => (
                          <option key={option.value || 'all'} value={option.value || ''}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
                    </div>
                  )}
                </div>

                {!isOpenedGroupFocus && !showFarmersCatalog && (
                  <div
                    className={`grid gap-1.5 ${
                      isAdminUser ? 'grid-cols-3' : !isLguUser ? 'grid-cols-2' : 'grid-cols-1'
                    }`}
                  >
                    {isAdminUser && (
                      <div className="relative min-w-0">
                        <select
                          aria-label="Filter by province"
                          value={selectedProvinceId}
                          onChange={(e) => {
                            setSelectedProvinceId(e.target.value);
                            setSelectedMunicipalityId('all');
                          }}
                          className={`${sb.input} px-1.5 pr-6 appearance-none truncate`}
                        >
                          <option value="all">All provinces</option>
                          {adminProvinceOptions.map((prov) => (
                            <option key={prov.province_id} value={prov.province_id}>
                              {prov.province_name}
                            </option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
                      </div>
                    )}

                    {!isLguUser && (
                      <div className="relative min-w-0">
                        <select
                          aria-label="Filter by municipality"
                          value={selectedMunicipalityId}
                          disabled={isAdminUser && selectedProvinceId === 'all'}
                          onChange={(e) => {
                            setSelectedMunicipalityId(e.target.value);
                          }}
                          className={`${sb.input} px-1.5 pr-6 appearance-none truncate disabled:opacity-60`}
                        >
                          <option value="all">
                            {isAdminUser && selectedProvinceId === 'all'
                              ? 'Select province first'
                              : adminLocationLoading
                                ? 'Loading…'
                                : 'All municipalities'}
                          </option>
                          {municipalityOptions.map((muni) => (
                            <option key={muni.id} value={muni.id}>
                              {muni.label}
                            </option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
                      </div>
                    )}

                    <div className="relative min-w-0">
                      <select
                        aria-label="Filter by risk level"
                        value={riskFilter}
                        onChange={(e) => setRiskFilter(e.target.value as FarmRiskFilter)}
                        className={`${sb.input} px-1.5 pr-6 appearance-none truncate`}
                      >
                        {RISK_FILTER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm"></i>
                    </div>
                  </div>
                )}

                {(usesOutboundFarmCatalog || isLguUser) && !isOpenedGroupFocus && !showFarmersCatalog && (
                  <label
                    className={`flex cursor-pointer items-start gap-2 rounded-lg border px-2.5 py-2 text-[11px] leading-snug ${
                      isDark
                        ? 'border-white/10 bg-white/[0.03] text-gray-200'
                        : 'border-[#e5e7eb] bg-[#f7f9f3] text-[#111827]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showFarmBoundaries}
                      onChange={(e) => setLoadFullFarmGeometry(e.target.checked)}
                      disabled={isLoading}
                      className="mt-0.5 shrink-0 rounded border-gray-400 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="font-semibold">Show all farm boundaries on map</span>
                  </label>
                )}
              </div>
            )}

            
            {showFarmersCatalog ? (
              isLoadingFarmers ? (
                <ApiListSkeleton count={5} label="Loading farmers" />
              ) : (
                <div
                  className={
                    isFarmListFocusMode ? 'flex min-h-0 flex-1 flex-col' : undefined
                  }
                >
                  {farmersError && (
                    <div
                      className={`mb-2 rounded-lg border px-2 py-1.5 text-[11px] ${
                        isDark
                          ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
                          : 'border-amber-200 bg-amber-50 text-amber-900'
                      }`}
                    >
                      {farmersError}
                    </div>
                  )}
                  <div
                    className={`space-y-2 ${
                      isFarmListFocusMode
                        ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4'
                        : 'overflow-y-auto max-h-96'
                    }`}
                  >
                    {currentFarmers.length > 0 ? (
                      currentFarmers.map((farmer) => {
                        const displayName = (farmer.name || farmer.label || `Farmer ${farmer.farmer_id}`).trim();
                        return (
                          <button
                            key={String(farmer.farmer_id)}
                            type="button"
                            onClick={() => {
                              setFarmerFilter({
                                farmer_id: farmer.farmer_id,
                                name: displayName,
                              });
                              setLguCatalogTab('fields');
                              setSearchQuery('');
                            }}
                            className={`w-full rounded-[12px] border px-2.5 py-2 text-left transition-colors ${
                              isDark
                                ? 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07]'
                                : 'border-[#d7e2d4] bg-white hover:bg-[#f4f7f1]'
                            }`}
                            title="Show this farmer's fields"
                          >
                            <div className="flex items-start gap-2">
                              <span
                                className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                                  isDark ? 'bg-emerald-500/20 text-emerald-200' : 'bg-[#008540]/10 text-[#008540]'
                                }`}
                              >
                                <i className="ri-user-line text-sm" aria-hidden />
                              </span>
                              <div className="min-w-0 flex-1">
                                <div
                                  className={`truncate text-xs font-semibold ${
                                    isDark ? 'text-gray-100' : 'text-gray-900'
                                  }`}
                                >
                                  {displayName}
                                </div>
                                <div
                                  className={`mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] ${
                                    isDark ? 'text-gray-400' : 'text-gray-500'
                                  }`}
                                >
                                  <span>ID: {farmer.farmer_id}</span>
                                  <span>{formatFarmerFieldsLabel(farmer.farms_count)}</span>
                                  <span>{formatFarmerMobileLabel(farmer.mobile)}</span>
                                </div>
                                {(farmer.city_name || farmer.province_name) && (
                                  <div
                                    className={`mt-0.5 truncate text-[10px] ${
                                      isDark ? 'text-gray-500' : 'text-gray-500'
                                    }`}
                                  >
                                    {[farmer.city_name, farmer.province_name].filter(Boolean).join(', ')}
                                  </div>
                                )}
                              </div>
                              <i
                                className={`ri-arrow-right-s-line shrink-0 text-base ${
                                  isDark ? 'text-gray-500' : 'text-gray-400'
                                }`}
                                aria-hidden
                              />
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className={`text-center py-4 text-sm ${sb.emptyText}`}>
                        {farmersError
                          ? 'Could not load farmers'
                          : 'No farmers found for this monitoring location'}
                      </div>
                    )}
                  </div>

                </div>
              )
            ) : isLoading && !isOpenedGroupFocus ? (
              <ApiListSkeleton count={5} label="Loading fields" />
            ) : (
              <div
                className={
                  isFarmListFocusMode ? 'flex min-h-0 flex-1 flex-col' : undefined
                }
              >
                {fieldsLoadError && (
                  <div
                    className={`mb-2 rounded-lg border px-3 py-2.5 text-[11px] ${
                      isDark
                        ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
                        : 'border-amber-200 bg-amber-50 text-amber-900'
                    }`}
                    role="alert"
                  >
                    <p className="font-semibold">Could not load farms</p>
                    <p className="mt-1 leading-snug opacity-90">{fieldsLoadError}</p>
                    <button
                      type="button"
                      onClick={() => void fetchFields()}
                      disabled={isLoading}
                      className={`mt-2 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                        isDark
                          ? 'bg-amber-500/20 text-amber-50 hover:bg-amber-500/30'
                          : 'bg-amber-100 text-amber-950 hover:bg-amber-200'
                      }`}
                    >
                      <i className="ri-refresh-line" aria-hidden />
                      Try again
                    </button>
                  </div>
                )}
                <div
                  ref={fieldsScrollCallbackRef}
                  onScroll={handleFieldsListScroll}
                  className={`space-y-2 ${
                    isFarmListFocusMode
                      ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4'
                      : 'overflow-y-auto max-h-96'
                  } ${
                    multiFieldSelectionLocked && isMultiFieldMode ? 'opacity-60 pointer-events-none' : ''
                  }`}
                >
                  {filteredFields.length > 0 ? (
                    filteredFields.map((field) => {
                      const isSingleSelected = isFieldSelected(field);
                      const isMultiSelected =
                        isMultiFieldMode &&
                        selectedMultiFieldIds.some((id) => String(id) === String(field.farm_id));
                      const isSelected = isSingleSelected || isMultiSelected;
                      const isDemoField = field.isDemo;
                      const isUnsaved = field.isUnsaved;
                      const fieldWeather = fieldWeatherById[String(field.farm_id)];
                      const fieldRisk = riskStatusById[String(field.farm_id)];
                      const fieldRiskLevel = riskLevelOf(fieldRisk);
                      const riskBadge =
                        isDemoField || isUnsaved
                          ? null
                          : riskBadgeMeta(fieldRiskLevel, isDark);
                      const blockCount = fieldBlockCount(field);
                      const blockRings = fieldBlockRings(field);
                      const isExpanded =
                        !isMultiFieldMode &&
                        expandedFarmId != null &&
                        String(expandedFarmId) === String(field.farm_id) &&
                        blockRings.length >= 1;
                      const selectedBlockIndex =
                        isSingleSelected && selectedField?.block_index != null
                          ? Number(selectedField.block_index)
                          : null;
                      
                      return (
                        <div 
                          key={field.farm_id} 
                          data-farm-id={
                            !isDemoField && !isUnsaved && field.farm_id != null
                              ? String(field.farm_id)
                              : undefined
                          }
                          className={`relative overflow-hidden rounded-[14px] cursor-pointer transition-all duration-200 ${
                            isSelected 
                              ? sb.fieldCardSelected
                              : isDemoField
                              ? sb.fieldCardDemo
                              : sb.fieldCard
                          }`}
                          onClick={() => handleFarmCardClick(field)}
                        >
                          {!isFarmGroupMode && (
                            <>
                              <WeatherAtmosphere weather={fieldWeather} />
                              <WeatherMoodStrip weather={fieldWeather} isDark={isDark} />
                            </>
                          )}

                          {isSelected && isDark && (
                            <>
                              <div className="pointer-events-none absolute -top-1 -right-1 z-20 w-4 h-4 bg-emerald-400 rounded-full flex items-center justify-center">
                                <i className="ri-check-line text-xs text-gray-900"></i>
                              </div>
                              <div className="pointer-events-none absolute inset-0 z-0 bg-emerald-400 rounded-lg opacity-10 animate-pulse"></div>
                            </>
                          )}

                          {isDemoField && !isSelected && isDark && (
                            <div className="pointer-events-none absolute -top-1 -right-1 z-20 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center">
                              <i className="ri-pencil-line text-xs text-gray-900"></i>
                            </div>
                          )}
                          
                          <div className="relative z-10 flex items-start gap-3 p-2.5">
                            {!isMultiFieldMode && (
                              <div
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                                  farmMarkerById[String(field.farm_id)] != null
                                    ? riskMarkerChipClass(
                                        isDemoField || isUnsaved ? null : fieldRiskLevel,
                                        isDark
                                      )
                                    : sb.toolIconWrap
                                }`}
                                title={
                                  farmMarkerById[String(field.farm_id)] != null
                                    ? [
                                        `Map marker #${farmMarkerById[String(field.farm_id)]}`,
                                        fieldRiskLevel
                                          ? riskSummaryText(fieldRisk)
                                          : 'No active risk alerts',
                                      ]
                                        .filter(Boolean)
                                        .join(' · ')
                                    : undefined
                                }
                              >
                                {farmMarkerById[String(field.farm_id)] != null ? (
                                  <span className="text-xs font-bold tabular-nums">
                                    {farmMarkerById[String(field.farm_id)]}
                                  </span>
                                ) : (
                                  <i className="ri-map-pin-user-line text-lg" aria-hidden />
                                )}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-2">
                                {isMultiFieldMode && (
                                  <div className="mt-0.5 shrink-0">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                      isMultiSelected
                                        ? 'bg-[#008540] border-[#008540] text-white'
                                        : isDark ? 'bg-transparent border-gray-400' : 'bg-transparent border-gray-300'
                                    }`}>
                                      {isMultiSelected && (
                                        <i className="ri-check-line text-xs text-white"></i>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-sm truncate ${
                                      isSelected ? sb.fieldNameSelected : sb.fieldName
                                    }`}>
                                      {displayFarmName(field as Record<string, unknown>)}
                                    </span>
                                    {blockCount != null && blockCount > 0 && (
                                      <span
                                        title={
                                          blockCount > 1
                                            ? `${blockCount} blocks — expand to choose Block 1, Block 2, …`
                                            : '1 polygon block in this field'
                                        }
                                        className={`shrink-0 inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                                          isSelected
                                            ? isDark
                                              ? 'bg-white/15 text-emerald-100'
                                              : 'bg-[#008540]/12 text-[#008540]'
                                            : isDark
                                              ? 'bg-white/10 text-gray-200'
                                              : 'bg-gray-100 text-gray-600'
                                        }`}
                                      >
                                        {blockCount} {blockCount === 1 ? 'block' : 'blocks'}
                                        {blockCount > 1 && (
                                          <i
                                            className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-[11px]`}
                                            aria-hidden
                                          />
                                        )}
                                      </span>
                                    )}
                                    {isSelected && isDark && (
                                      <span className="text-xs bg-emerald-400 text-gray-900 px-2 py-0.5 rounded-full font-medium shrink-0">
                                        ACTIVE
                                      </span>
                                    )}
                                    {isDemoField && !isSelected && isDark && (
                                      <span className="text-xs bg-blue-400 text-gray-900 px-2 py-0.5 rounded-full font-medium shrink-0">
                                        DEMO
                                      </span>
                                    )}
                                    {riskBadge && (
                                      <span
                                        title={riskSummaryText(fieldRisk)}
                                        className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${riskBadge.className}`}
                                      >
                                        {riskBadge.label}
                                      </span>
                                    )}
                                  </div>
                                  {(isMultiFieldMode ||
                                    isDemoField ||
                                    (blockCount != null && blockCount >= 1 && !isExpanded)) && (
                                    <div className={`text-xs mt-0.5 ${
                                      isSelected ? sb.fieldSubSelected : sb.fieldSub
                                    }`}>
                                      {isMultiFieldMode
                                        ? (isMultiSelected
                                            ? 'Selected for group'
                                            : 'Click to add to group')
                                        : isDemoField
                                          ? 'Demo field - click to analyze'
                                          : 'Click to expand and choose a block'}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {field.created_at && (
                                <div className={`text-[11px] mt-0.5 ${
                                  isSelected ? sb.fieldSubSelected : sb.fieldSub
                                }`}>
                                  <i className="ri-calendar-line mr-1" aria-hidden />
                                  {new Date(field.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {isUnsaved && (
                                <span className={`text-xs flex items-center ${isDark ? 'text-yellow-400' : 'text-amber-600'}`}>
                                  <i className="ri-save-line mr-1"></i>
                                  Unsaved
                                </span>
                              )}
                              <div className="flex items-center gap-2">
                                <span className={`text-xs whitespace-nowrap font-medium ${
                                  isSelected ? sb.fieldIdSelected : sb.fieldId
                                }`}>
                                  ID: {farmPublicId(field) || field.farm_id}
                                </span>
                                {!isDemoField && !isLguUser && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDeleteModal(field.farm_id);
                                    }}
                                    className="text-xs text-red-400 hover:text-red-300 transition-colors"
                                    title="Delete Field"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Multi-block list: nested under the field card */}
                          {isExpanded && (
                            <div
                              className={`relative z-10 mx-2.5 mb-2.5 mt-1 rounded-[10px] border-l-[3px] py-2 pl-3 pr-1.5 ${
                                isDark
                                  ? 'border-emerald-500/35 bg-black/25'
                                  : 'border-[#008540]/30 bg-[#f4f7f0]'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                className={`mb-1.5 pl-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                  isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}
                              >
                                Blocks
                              </div>
                              <div
                                className={`space-y-1 border-l pl-2.5 ${
                                  isDark ? 'border-white/10' : 'border-[#008540]/15'
                                }`}
                              >
                                {blockRings.map((ring, blockIndex) => {
                                  const blockNumber = blockIndex + 1;
                                  const blockLabel = displayBlockLabel(blockNumber);
                                  const isBlockActive =
                                    selectedBlockIndex != null &&
                                    selectedBlockIndex === blockIndex;
                                  return (
                                    <div
                                      key={`${field.farm_id}-block-${blockIndex}`}
                                      className={`relative pl-2 before:absolute before:-left-2.5 before:top-1/2 before:h-px before:w-2.5 before:-translate-y-1/2 before:content-[''] ${
                                        isDark ? 'before:bg-white/20' : 'before:bg-[#008540]/25'
                                      }`}
                                    >
                                      <div
                                        className={`flex items-stretch gap-1 rounded-[8px] ${
                                          isBlockActive
                                            ? isDark
                                              ? 'bg-emerald-500/25 ring-1 ring-emerald-400/40'
                                              : 'bg-[#008540]/12 ring-1 ring-[#008540]/25'
                                            : isDark
                                              ? 'bg-white/[0.04]'
                                              : 'bg-white/80'
                                        }`}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => handleBlockClick(field, blockIndex, ring)}
                                          className={`flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors ${
                                            isBlockActive
                                              ? isDark
                                                ? 'text-emerald-100'
                                                : 'text-[#008540]'
                                              : isDark
                                                ? 'text-gray-200 hover:bg-white/10'
                                                : 'text-gray-700 hover:bg-gray-50'
                                          } rounded-[8px]`}
                                        >
                                          <i
                                            className={`ri-shape-line text-[13px] ${
                                              isBlockActive
                                                ? isDark
                                                  ? 'text-emerald-300'
                                                  : 'text-[#008540]'
                                                : isDark
                                                  ? 'text-gray-500'
                                                  : 'text-gray-400'
                                            }`}
                                            aria-hidden
                                          />
                                          <span className="min-w-0 flex-1 truncate font-medium">
                                            {blockLabel}
                                          </span>
                                          {isBlockActive && (
                                            <span className="shrink-0 text-[10px] font-semibold uppercase opacity-80">
                                              Active
                                            </span>
                                          )}
                                        </button>
                                        {!isLguUser && (
                                          <button
                                            type="button"
                                            title={`Split ${blockLabel} into multiple blocks`}
                                            disabled={isSplittingFarm || isSavingFarmSplit || !!farmSplitReview}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onSplitFarm?.({
                                                ...field,
                                                block_index: blockIndex,
                                                block_number: blockNumber,
                                                block_label: displayBlockContextLabel(
                                                  field as Record<string, unknown>,
                                                  blockNumber
                                                ),
                                                polygons: [ring],
                                                _allPolygons: blockRings,
                                                geometry: { type: 'Polygon', coordinates: [ring] },
                                              });
                                            }}
                                            className={`shrink-0 self-center mr-1 inline-flex items-center gap-0.5 rounded-[8px] px-1.5 py-1 text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sb.quietChip}`}
                                          >
                                            <i
                                              className={`${isSplittingFarm ? 'ri-loader-4-line animate-spin' : 'ri-scissors-cut-line'} text-[11px]`}
                                              aria-hidden
                                            />
                                            Split
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Demo field actions */}
                          {isDemoField && (
                            <div className={`relative z-10 mt-2 pt-2 border-t ${isDark ? 'border-blue-500/30' : 'border-blue-200'}`}>
                              {agLandWarning && (
                                <div
                                  className={`mb-2 rounded-md px-2 py-1.5 text-[11px] leading-snug ${
                                    isDark
                                      ? 'bg-amber-500/15 text-amber-100 border border-amber-400/30'
                                      : 'bg-amber-50 text-amber-900 border border-amber-200'
                                  }`}
                                  title={agLandWarning}
                                  role="status"
                                >
                                  <div className="flex items-start gap-1.5">
                                    <i className="ri-error-warning-line text-sm mt-0.5 shrink-0" aria-hidden />
                                    <span>{agLandWarning}</span>
                                  </div>
                                </div>
                              )}
                              <div className="flex items-center justify-between space-x-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSaveDemoField?.();
                                  }}
                                  className={`flex-1 text-xs px-2 py-1.5 rounded-[8px] flex items-center justify-center font-medium transition-colors ${sb.primaryBtn}`}
                                  title={
                                    agLandWarning
                                      ? 'You can still save this as agricultural area; later you can delete it'
                                      : 'Save Field'
                                  }
                                >
                                  <i className="ri-save-line mr-1"></i>
                                  Save Field
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteModal('demo_field');
                                  }}
                                  className="text-xs bg-red-600 hover:bg-red-500 text-white px-2 py-1.5 rounded flex items-center justify-center"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {isSingleSelected && !isDemoField && (
                            <div className="relative z-10 mt-1.5 flex items-center justify-between gap-2 min-w-0">
                              <span
                                className={`text-[10px] font-medium shrink-0 ${
                                  isDark ? 'text-emerald-200' : 'text-emerald-700'
                                }`}
                              >
                                Under analysis
                              </span>
                              {!isLguUser && (
                                <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                                  {/^\d+$/.test(String(field.farm_id ?? '')) && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openBriefDialog(field);
                                      }}
                                      disabled={!!briefDownloadLang}
                                      title="Download today's farm brief"
                                      className={`inline-flex items-center gap-0.5 py-0.5 px-1.5 rounded-[8px] text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sb.quietChip}`}
                                    >
                                      <i
                                        className={`${
                                          briefDownloadLang && briefDialog?.farm_id === String(field.farm_id)
                                            ? 'ri-loader-4-line animate-spin'
                                            : 'ri-download-2-line'
                                        } text-[11px]`}
                                        aria-hidden
                                      />
                                      <span>Report</span>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEditBoundary?.(field);
                                    }}
                                    disabled={isSplittingFarm || isSavingFarmSplit || !!farmSplitReview}
                                    title="Update field boundary"
                                    className={`inline-flex items-center gap-0.5 py-0.5 px-1.5 rounded-[8px] text-[10px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sb.quietChip}`}
                                  >
                                    <i className="ri-edit-line text-[11px]" aria-hidden />
                                    <span>Update field</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : null}
                  {isLoadingMoreFields && (
                    <div className="py-2" aria-live="polite" aria-busy="true">
                      <ApiListSkeleton count={2} label="Loading more farms" />
                    </div>
                  )}
                  {filteredFields.length > 0 ? null : !isLoading && fieldsLoadError && fields.length === 0 ? (
                    <div
                      className={`rounded-[12px] border px-3 py-4 text-center ${
                        isDark
                          ? 'border-amber-500/30 bg-amber-500/10'
                          : 'border-amber-200 bg-amber-50'
                      }`}
                      role="alert"
                    >
                      <p className={`text-sm font-semibold ${isDark ? 'text-amber-100' : 'text-amber-950'}`}>
                        Farms could not be loaded
                      </p>
                      <p className={`mt-1 text-[11px] leading-snug ${isDark ? 'text-amber-100/90' : 'text-amber-900/90'}`}>
                        {fieldsLoadError}
                      </p>
                      <button
                        type="button"
                        onClick={() => void fetchFields()}
                        disabled={isLoading}
                        className={`mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
                          isDark
                            ? 'bg-amber-500/25 text-amber-50 hover:bg-amber-500/35'
                            : 'bg-amber-100 text-amber-950 hover:bg-amber-200'
                        }`}
                      >
                        <i className="ri-refresh-line text-sm" aria-hidden />
                        Try again
                      </button>
                    </div>
                  ) : !isLoading && fields.length === 0 && !fieldsLoadError && !showFarmersCatalog ? (
                    <div
                      className={`rounded-[12px] border px-3 py-4 text-center ${
                        isDark
                          ? 'border-[#2d3748] bg-white/[0.03]'
                          : 'border-[#e5e7eb] bg-[#f7f9f3]'
                      }`}
                    >
                      <p className={`text-sm font-semibold ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}>
                        No farms yet
                      </p>
                      <p className={`mt-1 text-[11px] leading-snug ${sb.emptyText}`}>
                        {isLguUser
                          ? `Create a field with Draw, AI delineation, or Upload — then save it to a farmer${
                              lguMonitoringLabel ? ` in ${lguMonitoringLabel}` : ' in your monitoring area'
                            }.`
                          : 'Create your first field with Draw, AI delineation, or Upload in Field tools — then save it to see it here and on the map.'}
                      </p>
                      {onStartCreateFarmTour && (
                        <button
                          type="button"
                          onClick={onStartCreateFarmTour}
                          className={`mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                            isDark
                              ? 'bg-[#008540] text-white hover:bg-[#097a34]'
                              : 'bg-[#008540] text-white hover:bg-[#0f5a2e]'
                          }`}
                        >
                          <i className="ri-guide-line text-sm" aria-hidden />
                          Show me how
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className={`text-center py-4 text-sm ${sb.emptyText}`}>
                      {isLoading
                        ? 'Loading fields…'
                        : 'No fields found with current filters'}
                    </div>
                  )}
                </div>


              </div>
            )}
          </div>
          )}

          {/* Save farm group modal */}
          {showSaveGroupModal && (
            <div
              className={cd.overlay}
              onClick={() => {
                if (!saveGroupBusy) setShowSaveGroupModal(false);
              }}
            >
              <div
                className={cd.panel}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="save-farm-group-title"
              >
                <h3 id="save-farm-group-title" className={cd.title}>
                  Save farm group
                </h3>
                <p className={cd.body}>
                  Save {selectedMultiFields.length} farm
                  {selectedMultiFields.length === 1 ? '' : 's'} for later bulk analysis.
                </p>
                <label className={`block text-xs mb-1 ${sb.countText}`} htmlFor="farm-group-name">
                  Group name
                </label>
                <input
                  id="farm-group-name"
                  type="text"
                  value={saveGroupName}
                  onChange={(e) => setSaveGroupName(e.target.value)}
                  placeholder=""
                  className={`${sb.input} mb-2`}
                  maxLength={255}
                  disabled={saveGroupBusy}
                />
                <label className={`block text-xs mb-1 ${sb.countText}`} htmlFor="farm-group-desc">
                  Description (optional)
                </label>
                <input
                  id="farm-group-desc"
                  type="text"
                  value={saveGroupDescription}
                  onChange={(e) => setSaveGroupDescription(e.target.value)}
                  placeholder=""
                  className={`${sb.input} mb-2`}
                  maxLength={1024}
                  disabled={saveGroupBusy}
                />
                {saveGroupError && (
                  <p className={`mb-2 text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                    {saveGroupError}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowSaveGroupModal(false)}
                    className={cd.cancelBtn}
                    disabled={saveGroupBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveFarmGroup()}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#008540] rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                    disabled={saveGroupBusy}
                  >
                    {saveGroupBusy ? 'Saving…' : 'Save group'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {briefDialog && (
            <div
              className={cd.overlay}
              onClick={() => {
                if (!briefDownloadLang) setBriefDialog(null);
              }}
            >
              <div
                className={`${cd.panel.replace('max-w-sm', 'max-w-md')} max-h-[min(90vh,720px)] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="farm-brief-dialog-title"
              >
                <h3 id="farm-brief-dialog-title" className={cd.title}>
                  Your farm brief
                </h3>
                <p className={cd.body}>
                  See the next 5 days on this field: rain, heat, and wind, where drought or pests need attention, and the best days to spray, water, or work the field.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    ['en', 'English'],
                    ['fil', 'Filipino'],
                  ] as const).map(([lang, label]) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => void downloadFieldBrief(lang)}
                      disabled={!!briefDownloadLang}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#008540] rounded-[10px] hover:opacity-90 transition-colors disabled:opacity-50"
                    >
                      <i
                        className={briefDownloadLang === lang ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'}
                        aria-hidden
                      />
                      {label}
                    </button>
                  ))}
                </div>
                {briefDialogError && (
                  <p className={`mt-3 text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`}>{briefDialogError}</p>
                )}
                <FarmReportEmailSchedule
                  farmId={briefDialog.farm_id}
                  farmName={briefDialog.farm_name}
                  labelClass={isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}
                  variant="popup"
                />
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setBriefDialog(null)}
                    className={cd.cancelBtn}
                    disabled={!!briefDownloadLang}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div
              className={cd.overlay}
              onClick={closeDeleteModal}
            >
              <div
                className={cd.panel}
                onClick={(e) => e.stopPropagation()}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="delete-field-dialog-title"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={cd.warnIconWrap} aria-hidden>
                    <i className="ri-delete-bin-line text-xl" />
                  </div>
                  <h3 id="delete-field-dialog-title" className={`${cd.title} mb-0 pt-1.5`}>
                    {fieldToDelete === 'demo_field' ? 'Delete Demo Field?' : 'Delete Field?'}
                  </h3>
                </div>
                <p className={cd.body}>
                  {fieldToDelete === 'demo_field'
                    ? 'This will remove the demo field from the map. This action cannot be undone.'
                    : 'This will permanently delete this field. This action cannot be undone.'}
                </p>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={closeDeleteModal} className={cd.cancelBtn}>
                    Cancel
                  </button>
                  <button type="button" onClick={confirmDeleteField} className={cd.dangerBtn}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Click outside handler for file upload menu */}
          {showFileUploadMenu && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => {
                setShowFileUploadMenu(false);
                setUploadFormatHelpId(null);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}